
import {
  ArrowLeft,
  Edit,
  FileText,
  Bed,
  Bath,
  Car,
  Calendar,
  View,
  MapPin,
  Phone,
  Mail,
  User,
  Tag,
  Eye,
  Trash2,
  Plus,
  PauseCircle,
  CheckCircle2,
  XCircle,
  
  ChevronDown,
  Building2,
  ExternalLink,
  Users2,
  X,
  DollarSign
} from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import {
  formatMelbourneDatePart,
  formatMelbourneTimePart,
  datetimeLocalToUtcISOString,
} from "../../../utils/melbourneTime";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../../components/ui";
import PropertyDocumentsPage from "../../../components/properties/ViewDocuments";
import { useEffect, useState } from "react";
import { getCollingsProperty, updateCollingsProperty, patchCollingsProperty, reorderCollingsPropertyImages } from "../../../api/collingsProperties";
import ManageGalleryDialog from "../../../components/properties/ManageGalleryDialog";
import { applyImageOrder } from "../../../utils/propertyImages";
import { getPublicPropertyById, patchPublicProperty, addAdditionalOwner, deleteAdditionalOwner } from "../../../api/publicApi";
import { getAgents, getPropertyNotes, createPropertyNote, deletePropertyNote, getProperty, updateProperty, patchProperty, reorderPropertyImages, uploadStatementOfInfo, deletePropertyDocument, deleteProperty, getProperties, withdrawProperty } from "../../../api/agent";
import { getPropertyEnquiries, updateEnquiry, getPropertyBidderRegistrations, getPropertyOffers, getMatchedBuyersForProperty } from "../../../api/propertyManagement";
import LeadPreviewModal from "../../../components/leads/LeadPreviewModal";
import { withdrawREAPropertyFromREA } from "../../../api/rea";
import PropertyWithdrawControls from "../../../components/common/PropertyWithdrawControls";
import { useSelector } from "react-redux";
import LazyImage from "../../../components/common/LazyImage";
import toast from "react-hot-toast";
import { invalidateCache } from "../../../lib/cache";
import ConfirmModal from "../../../components/common/ConfirmModal";
import MarkSoldModal from "../../../components/common/MarkSoldModal";
import SoldThroughPortalToggle from "../../../components/common/SoldThroughPortalToggle";
import AddOwnerModal from "../../../components/properties/modals/AddOwnerModal";
import AddPurchaserModal from "../../../components/properties/modals/AddPurchaserModal";
import { offerStatusPillClasses } from "../../offers/utils/offerStatusStyles";
import { useRef } from "react";
import { hasAdminAccess } from "../../../utils/roleAccess";
import AgentNotesSummary from "../../../components/notes/AgentNotesSummary";

// Helpers
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { style: "currency", currency: "AUD" });
};

const formatPrice = (price) => {
  if (!price) return null;
  const num = Number(price);
  if (isNaN(num)) return price;
  return `$${Math.round(num).toLocaleString()}`;
};

// Get price type label from price_display
const getPriceTypeLabel = (priceDisplay) => {
  const labels = {
    'exact': 'Asking Price',
    'range': 'Price Range',
    'contact': 'Contact Agent',
    'auction': 'Guided Price'
  };
  return labels[priceDisplay] || 'Asking Price';
};

// Format price display with type label
const formatPriceDisplay = (property) => {
  if (!property) return 'N/A';

  const priceDisplay = property.price_display || 'exact';
  const priceTypeLabel = getPriceTypeLabel(priceDisplay);

  // Always use the actual price value, not price_display_text
  // Get price value (check both price and price_from)
  const priceValue = property.price || property.price_from;

  // Price Range: Show "Price Range: value-value"
  if (priceDisplay === 'range') {
    const priceTo = property.price_to;
    if (priceValue && priceTo && Number(priceTo) !== Number(priceValue)) {
      return `${priceTypeLabel}: ${formatPrice(priceValue)} – ${formatPrice(priceTo)}`;
    } else if (priceValue) {
      return `${priceTypeLabel}: ${formatPrice(priceValue)}`;
    }
    return `${priceTypeLabel}`;
  }

  // Contact Agent: Show only "Contact Agent" (no value)
  if (priceDisplay === 'contact') {
    return `${priceTypeLabel}`;
  }

  // Auction: Show "Guided Price: value" if price exists
  if (priceDisplay === 'auction') {
    if (priceValue) {
      return `${priceTypeLabel}: ${formatPrice(priceValue)}`;
    }
    return `${priceTypeLabel}`;
  }

  // Asking Price (exact): Show "Asking Price: value"
  if (priceDisplay === 'exact') {
    if (priceValue) {
      return `${priceTypeLabel}: ${formatPrice(priceValue)}`;
    }
    return `${priceTypeLabel}: Contact Agent`;
  }

  // Fallback
  if (!priceValue) {
    return `${priceTypeLabel}: Contact Agent`;
  }

  return `${priceTypeLabel}: ${formatPrice(priceValue)}`;
};

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString();
  } catch (e) {
    return iso;
  }
};

const getStatusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (["for sale", "sale", "active", "under contract"].includes(s)) {
    return "bg-green-100 text-green-700";
  }
  if (["for rent", "rent", "rent out"].includes(s)) {
    return "bg-purple-100 text-purple-700";
  }
  if (["pending"].includes(s)) {
    return "bg-yellow-100 text-yellow-700";
  }
  if (["sold", "leased"].includes(s)) {
    return "bg-blue-100 text-blue-700";
  }
  if (["withdrawn"].includes(s)) {
    return "bg-slate-200 text-slate-700";
  }
  return "bg-slate-100 text-slate-700";
};

export default function PublicPropertyDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [property, setProperty] = useState(null);
  const isLease = property?.transaction_type === 'lease';
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('property');
  const [owners, setOwners] = useState([]);
  const [purchasers, setPurchasers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionEdit, setDescriptionEdit] = useState('');
  const [editForm, setEditForm] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [isMarkingSold, setIsMarkingSold] = useState(false);
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [showPurchaserModal, setShowPurchaserModal] = useState(false);
  const [soiUploading, setSoiUploading] = useState(false);
  const soiFileInputRef = useRef(null);

  // Second agent: selection and "Make Secondary on Listing?" (co_agent only when ticked)
  const [secondAgentId, setSecondAgentId] = useState('');
  const [makeSecondaryAgentOnListing, setMakeSecondaryAgentOnListing] = useState(false);

  useEffect(() => {
    if (property) {
      const coId = property?.co_agent?.id ?? property?.co_agent ?? '';
      setSecondAgentId(coId ? String(coId) : '');
      setMakeSecondaryAgentOnListing(!!coId);
    }
  }, [property?.id, property?.co_agent]);

  // Buyer Portal Interactions State
  const [enquiries, setEnquiries] = useState([]);
  const [propertyNotes, setPropertyNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [bidderRegistrations, setBidderRegistrations] = useState([]);
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [viewingOffer, setViewingOffer] = useState(null);
  const [isUpdatingOffer, setIsUpdatingOffer] = useState(false);
  const [interactionsLoading, setInteractionsLoading] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [enquiryEditMessage, setEnquiryEditMessage] = useState('');
  const [enquiryEditAgentNotes, setEnquiryEditAgentNotes] = useState('');
  const [isUpdatingEnquiry, setIsUpdatingEnquiry] = useState(false);

  // Buyer Matching State
  const [buyerMatches, setBuyerMatches] = useState([]);
  const [buyerMatchesLoading, setBuyerMatchesLoading] = useState(false);

  // Gallery Management
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [updatingGallery, setUpdatingGallery] = useState(false);
  const fileInputRef = useRef(null);
  const targetImageForReplacement = useRef(null);


  // Fetch agents for dropdowns
  useEffect(() => {
    const fetchAgentsList = async () => {
      setAgentsLoading(true);
      try {
        const res = await getAgents({ page_size: 100 });
        const agentsData = res?.data?.results || res?.data || [];
        setAgents(agentsData);
      } catch (error) {
        console.error("Failed to fetch agents:", error);
      } finally {
        setAgentsLoading(false);
      }
    };
    fetchAgentsList();
  }, []);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    getPublicPropertyById(params.id)
      .then((res) => {
        if (!isMounted) return;
        const rawData = res?.data || null;

        if (rawData) {
          const transformedData = {
            ...rawData,
            address: rawData.address || rawData.title || "No Address",
            price: rawData.price,
            price_display_text: rawData.price_display_text || (rawData.price > 0 ? `$${Number(rawData.price).toLocaleString()}` : 'Contact Agent'),
            title: rawData.title,
            beds: rawData.beds ?? 0,
            baths: rawData.baths ?? 0,
            parkings: rawData.parkings ?? 0,
            garages: rawData.parkings ?? rawData.garages ?? 0,
            parking_spaces: rawData.parkings ?? rawData.garages ?? 0,
            car_ports: rawData.car_ports ?? 0,
            open_spaces: rawData.open_spaces ?? rawData.open_space ?? 0,
            open_space: rawData.open_spaces ?? rawData.open_space ?? 0,
            floor_area: rawData.area || 0,
            land_area: rawData.area || 0,
            images: rawData.images || [],
            property_type: rawData.property_type,
            created_at: rawData.created_at,
            description: rawData.description,
            est_gross_yield: rawData.est_gross_yield || rawData.extra_info?.est_gross_yield || 0,
            est_outgoings: rawData.est_outgoings || rawData.extra_info?.est_outgoings || 0,
            est_net_yield: rawData.est_net_yield || rawData.extra_info?.est_net_yield || 0,
          };
          setProperty(transformedData);
        } else {
          setProperty(null);
        }

        // Build owners list: primary owner + additional owners
        const allOwners = [];
        const primaryOwner = rawData?.owner;
        if (primaryOwner && (primaryOwner.name || primaryOwner.contact || primaryOwner.email)) {
          allOwners.push({ ...primaryOwner, isPrimary: true });
        }
        const additionalOwners = rawData?.additional_owners || [];
        additionalOwners.forEach(ao => allOwners.push({ ...ao, isPrimary: false }));
        setOwners(allOwners);

        setError(null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err?.response?.data?.detail || "Failed to load property");
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [params.id]);

  // Access control: the Enquiries and Offers tabs (a listing's buyer data)
  // are visible only to the listing's primary agent, secondary agent, or an
  // admin. Every other agent — including the listing's tertiary agent and PA
  // accounts — is blocked. The backend enforces the same rule; this just
  // keeps the tabs from rendering for users who would only get a 403.
  const canViewBuyerData = (() => {
    if (!user) return false;
    const isAdmin =
      hasAdminAccess(user) ||
      (Array.isArray(user.secondary_roles) && user.secondary_roles.includes('admin')) ||
      user.is_superuser === true ||
      user.is_staff === true;
    if (isAdmin) return true;
    if (!property) return false;
    const agentId = property.agent?.id ?? property.agent_id ?? property.agent;
    const coAgentId = property.co_agent?.id ?? property.co_agent_id ?? property.co_agent;
    return user.id === agentId || user.id === coAgentId;
  })();

  // If a non-permitted user lands on a buyer-data tab, send them back to the
  // Property tab once the listing (and therefore access) is known.
  useEffect(() => {
    if (property && !canViewBuyerData && (activeTab === 'enquiries' || activeTab === 'offers')) {
      setActiveTab('property');
    }
  }, [property, canViewBuyerData, activeTab]);

  // Fetch property interactions
  useEffect(() => {
    const fetchInteractions = async () => {
      if (!params.id || !canViewBuyerData) return;
      setInteractionsLoading(true);
      try {
        const numericId = params.id.replace('crm-', '');
        const [enquiriesRes, registrationsRes, offersRes] = await Promise.allSettled([
          getPropertyEnquiries(numericId),
          getPropertyBidderRegistrations(numericId),
          getPropertyOffers(numericId)
        ]);

        if (enquiriesRes.status === 'fulfilled') {
          const rawEnquiries = enquiriesRes.value?.data?.results || [];
          const seen = new Set();
          const deduplicated = rawEnquiries.filter(enq => {
            const email = (enq.email || '').toLowerCase().trim();
            let msg = (enq.message || '').trim();
            if (msg.startsWith('[Buyer Portal') && msg.includes(']\n\n')) {
              msg = msg.split(']\n\n')[1]?.trim() || '';
            }
            if (msg.toLowerCase() === 'no message' || msg.toLowerCase() === 'no message.') msg = '';

            const key = `${email}|${msg}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          setEnquiries(deduplicated);
        }
        if (registrationsRes.status === 'fulfilled') {
          setBidderRegistrations(registrationsRes.value?.data?.results || []);
        }
        if (offersRes.status === 'fulfilled') {
          const rawOffers = offersRes.value?.data?.results || [];
          const seen = new Set();
          const deduplicated = rawOffers.filter(offer => {
            const email = (offer.buyer_email || offer.email || '').toLowerCase().trim();
            const msg = (offer.message || offer.message_to_agent || '').trim();

            // Extract logical reference
            let trueRef = offer.reference_number;
            const refMatch = msg.match(/OFF-\d{4}-\d{5}/);
            if (refMatch) trueRef = refMatch[0];

            const key = `${email}|${trueRef}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          setOffers(deduplicated);
        }
      } catch (error) {
        console.error("Failed to fetch property interactions:", error);
      } finally {
        setInteractionsLoading(false);
      }
    };
    fetchInteractions();
    fetchPropertyNotes();
  }, [params.id, canViewBuyerData]);

  // Fetch Buyer Matches when tab is active
  useEffect(() => {
    if (activeTab === 'buyer_match' && params.id) {
      const fetchMatches = async () => {
        setBuyerMatchesLoading(true);
        try {
          const numericId = params.id.replace('crm-', '');
          const res = await getMatchedBuyersForProperty(numericId);
          setBuyerMatches(res.data.matched_buyers || []);
        } catch (error) {
          console.error("Failed to fetch buyer matches:", error);
          toast.error("Failed to load buyer matches");
        } finally {
          setBuyerMatchesLoading(false);
        }
      };

      fetchMatches();
    }
  }, [activeTab, params.id]);

  const fetchPropertyNotes = async () => {
    if (!params.id) return;
    setNotesLoading(true);
    try {
      const numericId = params.id.replace('crm-', '');
      const res = await getPropertyNotes(numericId);
      setPropertyNotes(res.data?.results || res.data || []);
    } catch (err) {
      console.error("Failed to fetch notes", err);
    } finally {
      setNotesLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!newNote.trim()) return;

    setIsUpdating(true);
    try {
      const numericId = params.id.replace('crm-', '');
      await createPropertyNote(numericId, { content: newNote });
      toast.success("Note added successfully");
      setNewNote("");
      fetchPropertyNotes();
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Failed to save note. Ensure property is registered in CRM.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    try {
      await deletePropertyNote(noteId);
      toast.success("Note deleted");
      fetchPropertyNotes();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete note");
    }
  };

  const handleUpdateEnquiry = async (formData) => {
    if (!editingEnquiry) return;
    setIsUpdatingEnquiry(true);
    try {
      const res = await updateEnquiry(editingEnquiry.id, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        agent_notes: formData.agent_notes,
        status: formData.status,
        property: formData.propertyId
      });

      const updatedEnq = res.data?.enquiry || {
        ...editingEnquiry,
        ...formData,
        type_display: editingEnquiry.type_display
      };

      setEnquiries(prev => prev.map(e => e.id === editingEnquiry.id ? updatedEnq : e));
      toast.success("Enquiry updated successfully");
      setEditingEnquiry(null);
    } catch (err) {
      toast.error("Failed to update enquiry");
    } finally {
      setIsUpdatingEnquiry(false);
    }
  };

  const MAX_SOI_SIZE_MB = 2;
  const validateSOIFile = (file) => {
    if (!file) return null;
    const isPdf = (file.name || "").toLowerCase().endsWith(".pdf") || file.type === "application/pdf";
    if (!isPdf) {
      toast.error("Only PDF files are accepted for Property Price Statement.");
      return false;
    }
    if (file.size > MAX_SOI_SIZE_MB * 1024 * 1024) {
      toast.error(`File size must be ${MAX_SOI_SIZE_MB}MB or less.`);
      return false;
    }
    return true;
  };
  const handleSOIUpload = async (files) => {
    const fileList = Array.isArray(files) ? files : (files ? [files] : []);
    const file = fileList[0];
    if (!file || !validateSOIFile(file)) return;
    setSoiUploading(true);
    try {
      await uploadStatementOfInfo(params.id, [file]);
      const res = await getPublicPropertyById(params.id);
      setProperty(res.data);
      toast.success("Property Price Statement uploaded successfully.");
      if (soiFileInputRef.current) soiFileInputRef.current.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setSoiUploading(false);
    }
  };
  // Every PPS upload is kept (CRM ticket 13177542710): the newest upload is
  // the current statement and the older ones stay listed as previous
  // versions, so there is never a reason to Remove a document first.
  const sortedSoiDocs = (property?.documents || [])
    .filter((d) => d.category === "statement_of_info_docs")
    .slice()
    .sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0));

  const handleReplaceSOI = () => soiFileInputRef.current?.click();
  const handleRemoveSOI = async () => {
    const soiDoc = sortedSoiDocs[0];
    if (!soiDoc) return;
    if (!window.confirm("Remove this Property Price Statement? You can upload a new one anytime.")) return;
    try {
      await deletePropertyDocument(soiDoc.id);
      const res = await getPublicPropertyById(params.id);
      setProperty(res.data);
      toast.success("Property Price Statement removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove document");
    }
  };

  const handleSaveOwner = async (data) => {
    try {
      setIsUpdating(true);
      const hasPrimaryOwner = owners.some(o => o.isPrimary);

      if (!hasPrimaryOwner) {
        // Set primary owner on the property record
        const payload = {
          owner_name: data.owner_name,
          owner_preferred_name: data.preferred_name || "",
          contact: data.contact,
          email: data.email
        };
        await patchPublicProperty(params.id, payload);
      } else {
        // Add as an additional owner
        await addAdditionalOwner(params.id, {
          name: data.owner_name,
          preferred_name: data.preferred_name || "",
          contact: data.contact,
          email: data.email
        });
      }

      // Reload fresh data
      const res = await getPublicPropertyById(params.id);
      setProperty(res.data);
      const allOwners = [];
      const primaryOwner = res.data?.owner;
      if (primaryOwner && (primaryOwner.name || primaryOwner.contact || primaryOwner.email)) {
        allOwners.push({ ...primaryOwner, isPrimary: true });
      }
      (res.data?.additional_owners || []).forEach(ao => allOwners.push({ ...ao, isPrimary: false }));
      setOwners(allOwners);

      toast.success("Owner added successfully");
      setShowOwnerModal(false);
    } catch (error) {
      console.error("Failed to save owner:", error);
      toast.error("Failed to add owner");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteOwner = async (owner) => {
    if (!window.confirm(`Remove ${owner.name} from this property?`)) return;
    try {
      setIsUpdating(true);
      if (owner.isPrimary) {
        // Clear primary owner fields
        await patchPublicProperty(params.id, { owner_name: '', contact: '', email: '' });
      } else {
        await deleteAdditionalOwner(params.id, owner.id);
      }
      const res = await getPublicPropertyById(params.id);
      setProperty(res.data);
      const allOwners = [];
      const primaryOwner = res.data?.owner;
      if (primaryOwner && (primaryOwner.name || primaryOwner.contact || primaryOwner.email)) {
        allOwners.push({ ...primaryOwner, isPrimary: true });
      }
      (res.data?.additional_owners || []).forEach(ao => allOwners.push({ ...ao, isPrimary: false }));
      setOwners(allOwners);
      toast.success("Owner removed");
    } catch (error) {
      console.error("Failed to remove owner:", error);
      toast.error("Failed to remove owner");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSavePurchaser = async (data) => {
    try {
      setIsUpdating(true);
      const payload = {
        purchaser_name: data.purchaser_name,
        purchaser_contact: data.purchaser_contact,
        purchaser_email: data.purchaser_email
      };
      await patchPublicProperty(params.id, payload);
      const res = await getPublicPropertyById(params.id);
      setProperty(res.data);
      if (res.data?.purchaser) {
        setPurchasers([res.data.purchaser]);
      }
      toast.success("Purchaser added successfully");
      setShowPurchaserModal(false);
    } catch (error) {
      console.error("Failed to save purchaser:", error);
      toast.error("Failed to add purchaser");
    } finally {
      setIsUpdating(false);
    }
  };

  // Gallery Management Logic
  const handleReplaceImage = (img) => {
    targetImageForReplacement.current = img;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !targetImageForReplacement.current) return;

    const imageToDelete = typeof targetImageForReplacement.current === 'object'
      ? targetImageForReplacement.current.file
      : targetImageForReplacement.current;

    const formData = new FormData();
    formData.append("images", file);
    formData.append("delete_images", JSON.stringify([imageToDelete]));

    try {
      setUpdatingGallery(true);
      const loadingToast = toast.loading("Replacing image...");
      if (params.id?.startsWith('crm-')) {
        const numericId = params.id.replace('crm-', '');
        await updateProperty(numericId, formData);
      } else {
        await updateCollingsProperty(params.id, formData);
      }
      const res = await getPublicPropertyById(params.id);
      setProperty(res.data);
      toast.dismiss(loadingToast);
      toast.success("Image replaced successfully");
    } catch (err) {
      toast.error("Failed to replace image");
    } finally {
      setUpdatingGallery(false);
      targetImageForReplacement.current = null;
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteImage = async (img) => {
    const imageToDelete = typeof img === 'object' ? img.file : img;
    const formData = new FormData();
    formData.append("delete_images", JSON.stringify([imageToDelete]));

    try {
      setUpdatingGallery(true);
      if (params.id?.startsWith('crm-')) {
        const numericId = params.id.replace('crm-', '');
        await updateProperty(numericId, formData);
      } else {
        await updateCollingsProperty(params.id, formData);
      }
      const res = await getPublicPropertyById(params.id);
      setProperty(res.data);
      toast.success("Image removed");
    } catch (err) {
      toast.error("Failed to remove image");
    } finally {
      setUpdatingGallery(false);
    }
  };

  // Upload portal-safe files picked in the Manage Gallery dialog (it vets the
  // pick itself before calling this).
  const uploadImages = async (files) => {
    if (!files || files.length === 0) return;

    const formData = new FormData();
    files.forEach(file => formData.append("images", file));

    try {
      setUpdatingGallery(true);
      const loadingToast = toast.loading("Uploading images...");
      if (params.id?.startsWith('crm-')) {
        const numericId = params.id.replace('crm-', '');
        await updateProperty(numericId, formData);
      } else {
        await updateCollingsProperty(params.id, formData);
      }
      const res = await getPublicPropertyById(params.id);
      setProperty(res.data);
      toast.dismiss(loadingToast);
      toast.success("Images uploaded successfully");
    } catch (err) {
      toast.error("Failed to upload images");
    } finally {
      setUpdatingGallery(false);
    }
  };

  // Persist a new photo order from the Manage Gallery dialog (it autosaves;
  // see ManageGalleryDialog). CRM-backed rows go to the CRM endpoint, the
  // rest to the Collings (REA) one.
  const saveImageOrder = async (reorderedUrls) => {
    if (params.id?.startsWith('crm-')) {
      const numericId = params.id.replace('crm-', '');
      await reorderPropertyImages(numericId, reorderedUrls);
    } else {
      await reorderCollingsPropertyImages(params.id, reorderedUrls);
    }
    setProperty((prev) =>
      prev ? { ...prev, images: applyImageOrder(prev.images, reorderedUrls) } : prev
    );
  };

  const handleEditInfo = () => {
    setEditForm({
      price: property.price || "",
      price_display_text: property.price_display_text || `Offers above ${property.price}`,
      weekly_rent: property.weekly_rent != null && property.weekly_rent !== "" ? String(property.weekly_rent) : "",
      property_type: property.property_type || "Residential",
      beds: property.beds ?? 0,
      baths: property.baths ?? 0,
      garages: property.garages ?? property.parkings ?? property.parking_spaces ?? 0,
      car_ports: property.car_ports ?? 0,
      open_spaces: property.open_spaces ?? property.open_space ?? 0,
      floor_area: property.floor_area || 0,
      land_area: property.land_area || 0,
    });
    setIsEditingInfo(true);
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveInfo = async () => {
    try {
      setIsUpdating(true);
      if (params.id?.startsWith('crm-') && !property?.rea_listing_id) {
        // Pure CRM property (no linked RealEstateProperty) - use property-management
        const numericId = params.id.replace('crm-', '');
        const payload = {
          price: editForm.price,
          price_display_text: editForm.price_display_text,
          // Send "" when cleared so patchProperty includes the key and backend can clear weekly_rent
          weekly_rent: editForm.weekly_rent !== "" && editForm.weekly_rent != null ? editForm.weekly_rent : "",
          property_type: editForm.property_type,
          beds: editForm.beds ?? property.beds ?? 0,
          baths: editForm.baths ?? property.baths ?? 0,
          parkings: editForm.garages ?? 0,
          car_ports: editForm.car_ports ?? 0,
          open_spaces: editForm.open_spaces ?? 0,
          area: editForm.floor_area || editForm.land_area || 0,
        };
        await patchProperty(numericId, payload);
      } else {
        // Collings/REA property - use property-crawler with mapped field names
        await updateCollingsProperty(params.id, {
          price: editForm.price || property.price || 0,
          property_type: editForm.property_type,
          weekly_rent:
            editForm.weekly_rent !== "" && editForm.weekly_rent != null
              ? editForm.weekly_rent
              : null,
          floor_area: editForm.floor_area || property.floor_area || 0,
          land_area: editForm.land_area || property.land_area || 0,
          parking_spaces: editForm.garages ?? property.garages ?? property.parkings ?? 0,
          garages: editForm.garages ?? property.garages ?? property.parkings ?? 0,
          car_ports: editForm.car_ports ?? property.car_ports ?? 0,
          open_spaces: editForm.open_spaces ?? property.open_spaces ?? property.open_space ?? 0,
          bedrooms: editForm.beds ?? property.beds ?? 0,
          bathrooms: editForm.baths ?? property.baths ?? 0,
          beds: editForm.beds ?? property.beds ?? 0,
          baths: editForm.baths ?? property.baths ?? 0,
          features: {
            ...(typeof property.features === 'object' && property.features !== null && !Array.isArray(property.features)
              ? property.features
              : {}),
            bedrooms: editForm.beds ?? property.beds ?? 0,
            bathrooms: editForm.baths ?? property.baths ?? 0,
            garages: editForm.garages ?? property.garages ?? property.parkings ?? 0,
            carports: editForm.car_ports ?? property.car_ports ?? 0,
            openSpaces: editForm.open_spaces ?? property.open_spaces ?? property.open_space ?? 0,
          },
          agent_id: property.agent_id ?? property.agent?.id,
          street: property.street || property.street_name || "",
          suburb: property.suburb || "",
          state: property.state || "",
          postcode: property.postcode || "",
          category: property.category || property.property_type || "",
          headline: property.headline ?? property.title ?? "",
          description: property.description ?? "",
        });
      }
      invalidateCache.publicProperties();
      invalidateCache.collingsProperties();
      const res = await getPublicPropertyById(params.id);
      const rawData = res?.data || null;
      if (rawData) {
        const transformedData = {
          ...rawData,
          address: rawData.address || rawData.title || "No Address",
          price: rawData.price,
          price_display_text: rawData.price_display_text || (rawData.price > 0 ? `$${Number(rawData.price).toLocaleString()}` : 'Contact Agent'),
          title: rawData.title,
          beds: rawData.beds ?? 0,
          baths: rawData.baths ?? 0,
          parkings: rawData.parkings ?? 0,
          garages: rawData.parkings ?? rawData.garages ?? 0,
          parking_spaces: rawData.parkings ?? rawData.garages ?? 0,
          car_ports: rawData.car_ports ?? 0,
          open_spaces: rawData.open_spaces ?? rawData.open_space ?? 0,
          open_space: rawData.open_spaces ?? rawData.open_space ?? 0,
          floor_area: rawData.area || 0,
          land_area: rawData.area || 0,
          images: rawData.images || [],
          property_type: rawData.property_type,
          created_at: rawData.created_at,
          description: rawData.description,
          est_gross_yield: rawData.est_gross_yield || rawData.extra_info?.est_gross_yield || 0,
          est_outgoings: rawData.est_outgoings || rawData.extra_info?.est_outgoings || 0,
          est_net_yield: rawData.est_net_yield || rawData.extra_info?.est_net_yield || 0,
        };
        setProperty(transformedData);
      }
      setIsEditingInfo(false);
      toast.success("Information updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditDescription = () => {
    setDescriptionEdit(property.description || '');
    setIsEditingDescription(true);
  };

  const handleSaveDescription = async () => {
    try {
      setIsUpdating(true);
      if (params.id?.startsWith('crm-') && !property?.rea_listing_id) {
        const numericId = params.id.replace('crm-', '');
        await patchProperty(numericId, { description: descriptionEdit });
      } else {
        await updateCollingsProperty(params.id, { description: descriptionEdit });
      }
      invalidateCache.publicProperties();
      invalidateCache.collingsProperties();
      const res = await getPublicPropertyById(params.id);
      const rawData = res?.data || null;
      if (rawData) {
        const transformedData = {
          ...property,
          ...rawData,
          address: rawData.address || rawData.title || "No Address",
          price: rawData.price,
          description: rawData.description,
          title: rawData.title,
          beds: rawData.beds || 0,
          baths: rawData.baths || 0,
          parkings: rawData.parkings || 0,
          images: rawData.images || [],
          property_type: rawData.property_type,
          created_at: rawData.created_at,
        };
        setProperty(transformedData);
      }
      setIsEditingDescription(false);
      toast.success("Description updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDescription = (description) => {
    if (!description) return ['No description available.'];
    let paragraphs = description.split(/\n\n+/);
    if (paragraphs.length === 1) {
      paragraphs = description.split(/\n/);
    }
    if (paragraphs.length === 1) {
      paragraphs = description.split(/\.\s+(?=[A-Z])/);
      paragraphs = paragraphs.map(p => p.trim() + (p.endsWith('.') ? '' : '.'));
    }
    return paragraphs.map(p => p.trim()).filter(p => p.length > 0);
  };


  const handlePropertyWithdraw = async (reason, reasonNote) => {
    if (property?.listing_id) {
      const reaResponse = await withdrawREAPropertyFromREA(params.id, reason, reasonNote);
      toast.success(reaResponse.data?.message || 'Property withdrawn from REA successfully');
    } else {
      await withdrawProperty(params.id, reason, reasonNote);
      toast.success('Property withdrawn successfully');
    }
    const res = await getPublicPropertyById(params.id);
    setProperty(res.data);
  };

  const handlePropertyRelist = async () => {
    await patchPublicProperty(params.id, { is_withdrawn: false });
    toast.success('Property relisted successfully');
    const res = await getPublicPropertyById(params.id);
    setProperty(res.data);
  };

  const handleToggleSold = async (soldDetails = null) => {
    setIsMarkingSold(true);
    // Check both is_sold field and status field
    const isCurrentlySold = property?.is_sold || property?.status === 'Sold' || property?.status?.name === 'Sold';
    try {
      const updateData = { is_sold: !isCurrentlySold };
      if (!isCurrentlySold && soldDetails && typeof soldDetails.soldPrice === 'number') {
        updateData.sold_price = soldDetails.soldPrice;
        updateData.suppress_sold_price = soldDetails.suppressSoldPrice;
        updateData.sold_through_collings_portal = Boolean(soldDetails.soldThroughCollingsPortal);
      }
      await patchPublicProperty(params.id, updateData);
      toast.success(isCurrentlySold ? 'Property marked as unsold successfully' : 'Property marked as sold successfully');
      // Invalidate listing caches so Public Properties page reflects new sold status immediately
      invalidateCache.publicProperties();
      invalidateCache.properties();
      // Refresh property data
      const res = await getPublicPropertyById(params.id);
      setProperty(res.data);
      setShowSoldModal(false);
    } catch (err) {
      toast.error('Failed to update sold status');
      console.error('Sold toggle error:', err);
    } finally {
      setIsMarkingSold(false);
    }
  };

  const handlePortalAttributionToggle = async (nextValue) => {
    await patchPublicProperty(params.id, { sold_through_collings_portal: nextValue });
    const res = await getPublicPropertyById(params.id);
    setProperty(res.data);
    toast.success(
      nextValue
        ? 'Marked as sold through the Collings portal'
        : 'Removed Collings portal attribution',
    );
  };

  const isPropertySold =
    property?.is_sold ||
    property?.status === 'Sold' ||
    property?.status?.name === 'Sold';

  const handleDeleteProperty = async () => {
    setIsDeleting(true);
    try {
      await deleteProperty(params.id);
      toast.success('Property deleted successfully');
      setShowDeleteModal(false);
      // Navigate back to property listing
      navigate('/public-property-search');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete property');
      console.error('Delete property error:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#55C5D0] mx-auto mb-4"></div>
          <p className="text-lg text-[#374151] font-medium">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-sm border border-gray-100 max-w-md">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-xl font-semibold text-[#374151] mb-2">{error || "Property Not Found"}</h3>
          <p className="text-[#374151] opacity-70 mb-6 font-medium">The property you're looking for doesn't exist or has been removed from our database.</p>
          <Link to="/properties">
            <Button className="w-full bg-[#55C5D0] hover:bg-[#4AADB7] text-white py-6 rounded-xl font-bold shadow-lg shadow-cyan-100 transition-all">
              Return to Listings
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const renderPropertyTab = () => (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4 sm:mb-8">
              <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#374151]">Property Images</h3>
              <button
                onClick={() => setIsGalleryOpen(true)}
                className="px-5 py-2 bg-[#ECECEC] hover:bg-gray-200 text-[#374151] rounded-md text-[11px] font-bold transition-all"
              >
                Edit Gallery
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
              {property?.images && property.images.length > 0 ? (
                property.images.map((img, idx) => (
                  <div
                    key={idx}
                    className="group relative aspect-square bg-gray-100 rounded-lg sm:rounded-xl overflow-hidden cursor-pointer border-2 border-transparent hover:border-[#55C5D0] transition-all duration-300"
                  >
                    <LazyImage
                      src={img?.file || img}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReplaceImage(img);
                        }}
                        className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 hover:bg-white hover:text-[#55C5D0]"
                      >
                        <Edit className="w-5 h-5 text-[#55C5D0]" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-3 aspect-[3/1] flex flex-col items-center justify-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <Eye className="w-12 h-12 text-[#374151] opacity-20 mb-2" />
                  <p className="text-[#374151] opacity-50 font-medium">No images available for this property</p>
                </div>
              )}
            </div>

            {/* Hidden File Input for Single Replacement */}
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />

            {/* Gallery Management Dialog (shared with the CRM and Collings
                property pages - see ManageGalleryDialog). */}
            <ManageGalleryDialog
              open={isGalleryOpen}
              onClose={() => setIsGalleryOpen(false)}
              images={property?.images || []}
              busy={updatingGallery}
              onReorder={saveImageOrder}
              onAddFiles={uploadImages}
              onDelete={handleDeleteImage}
            />

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              {(() => {
                const statusName = property?.status?.name || property?.status || '';
                const isOnMarket = statusName === 'On Market Listing';
                const isAppraisal = statusName === 'Appraisal';
                const isOffMarket = statusName === 'Off Market Listing';
                const updateVisibility = property?.id?.toString().startsWith('crm-')
                  ? (data) => patchPublicProperty(property.id, data)
                  : (data) => updateCollingsProperty(property.id, data);
                if (isAppraisal) {
                  return (
                    <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
                      <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#111827] mb-4">Appraisal – Pipeline</h3>
                      <p className="text-[11px] text-[#374151] opacity-70 mb-4">No visibility options. Sorted in your pipeline by type and temperature.</p>
                      <div className="space-y-2 text-[11px] text-[#374151]">
                        <p><span className="font-medium">Type:</span> {property?.appraisal_market_type === 'on_market' ? 'On-Market' : property?.appraisal_market_type === 'off_market' ? 'Off-Market' : '—'}</p>
                        <p><span className="font-medium">Temperature:</span> {property?.appraisal_temperature ? String(property.appraisal_temperature).charAt(0).toUpperCase() + String(property.appraisal_temperature).slice(1) : '—'}</p>
                      </div>
                    </div>
                  );
                }
                if (isOffMarket) {
                  return (
                    <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
                      <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#111827] mb-8">Visibility (Off-Market)</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between group">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={property?.published_on_website || false}
                              onChange={async (e) => {
                                try {
                                  await updateVisibility({ published_on_website: e.target.checked });
                                  setProperty(prev => ({ ...prev, published_on_website: e.target.checked }));
                                  toast.success(e.target.checked ? 'Published to Collings Portal' : 'Unpublished from Collings Portal');
                                } catch (err) {
                                  toast.error('Failed to update visibility');
                                }
                              }}
                              className="w-3.5 h-3.5 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] cursor-pointer"
                            />
                            <span className="text-[11px] text-[#374151] font-medium">Collings Portal</span>
                          </div>
                          <div className="flex items-center space-x-8">
                            <span className="text-[11px] text-[#374151] w-24 text-left opacity-70">Collings Portal only</span>
                            <div className="w-40 flex justify-start items-center space-x-2">
                              {property?.published_on_website ? (
                                <span className="text-[9px] px-2.5 py-0.5 rounded-full font-bold flex items-center shadow-sm whitespace-nowrap bg-green-100 text-green-600">
                                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5" />
                                  Active
                                </span>
                              ) : (
                                <span className="text-[11px] text-[#374151] opacity-50">Inactive</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {property?.published_on_website && (
                          <div className="pt-2 animate-in slide-in-from-top-1 duration-200">
                            <label className="block text-[11px] font-bold text-[#374151] mb-2">Launch Date & Time</label>
                            <input
                              type="datetime-local"
                              className="w-full text-[11px] border border-gray-100 rounded-lg px-3 py-2 outline-none focus:border-[#55C5D0] transition-all"
                              value={property?.listing_launch_at ? `${formatMelbourneDatePart(property.listing_launch_at)}T${formatMelbourneTimePart(property.listing_launch_at)}` : ''}
                              onChange={async (e) => {
                                const utc = datetimeLocalToUtcISOString(e.target.value);
                                try {
                                  await updateVisibility({ listing_launch_at: utc });
                                  setProperty(prev => ({ ...prev, listing_launch_at: utc }));
                                  toast.success('Launch date updated');
                                } catch (err) {
                                  toast.error('Failed to update launch date');
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                if (!isOnMarket) return null;
                return (
                  <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
                    <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#111827] mb-8">Visibility (On-Market)</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3 mb-2">
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] cursor-pointer"
                          checked={property?.internal_marketing && property?.published_on_rea && property?.published_on_domain && property?.published_on_collings}
                          onChange={async (e) => {
                            const checked = e.target.checked;
                            try {
                              await updateVisibility({
                                internal_marketing: checked,
                                published_on_rea: checked,
                                published_on_domain: checked,
                                published_on_collings: checked
                              });
                              setProperty(prev => ({ ...prev, internal_marketing: checked, published_on_rea: checked, published_on_domain: checked, published_on_collings: checked }));
                              toast.success(checked ? 'All portals enabled' : 'All portals disabled');
                            } catch (err) {
                              toast.error('Failed to update visibility');
                            }
                          }}
                        />
                        <span className="text-[11px] text-[#374151] opacity-50 font-medium">(tick all)</span>
                      </div>
                      <div className="flex items-center justify-between group">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={property?.internal_marketing || false}
                            onChange={async (e) => {
                              try {
                                await updateVisibility({ internal_marketing: e.target.checked });
                                setProperty(prev => ({ ...prev, internal_marketing: e.target.checked }));
                                toast.success(e.target.checked ? 'Enabled Internal Marketing' : 'Disabled Internal Marketing');
                              } catch (err) {
                                toast.error('Failed to update visibility');
                              }
                            }}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] cursor-pointer"
                          />
                          <span className="text-[11px] text-[#374151] font-medium">Internal Marketing</span>
                        </div>
                        <div className="w-40 flex justify-start items-center space-x-2">
                          {property?.internal_marketing ? (
                            <span className="text-[9px] px-2.5 py-0.5 rounded-full font-bold flex items-center shadow-sm whitespace-nowrap bg-green-100 text-green-600">
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5" />
                              Active
                            </span>
                          ) : (
                            <span className="text-[11px] text-[#374151] opacity-50">Inactive</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between group">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={property?.published_on_rea || false}
                            onChange={async (e) => {
                              try {
                                await updateVisibility({ published_on_rea: e.target.checked });
                                setProperty(prev => ({ ...prev, published_on_rea: e.target.checked }));
                                toast.success(e.target.checked ? 'Published to REA' : 'Unpublished from REA');
                              } catch (err) {
                                toast.error('Failed to update visibility');
                              }
                            }}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] cursor-pointer"
                          />
                          <span className="text-[11px] text-[#374151] font-medium">realestate.com.au</span>
                        </div>
                        <div className="flex items-center space-x-8">
                          <span className="text-[11px] text-[#374151] w-24 text-left opacity-70">{property?.rea_listing_id || '-'}</span>
                          <div className="w-40 flex justify-start items-center space-x-2">
                            {property?.published_on_rea ? (
                              <span className="text-[9px] px-2.5 py-0.5 rounded-full font-bold flex items-center shadow-sm whitespace-nowrap bg-cyan-100 text-[#55C5D0]">Published</span>
                            ) : (
                              <span className="text-[11px] text-[#374151] opacity-50">-</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between group">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={property?.published_on_domain || false}
                            onChange={async (e) => {
                              try {
                                await updateVisibility({ published_on_domain: e.target.checked });
                                setProperty(prev => ({ ...prev, published_on_domain: e.target.checked }));
                                toast.success(e.target.checked ? 'Published to Domain' : 'Unpublished from Domain');
                              } catch (err) {
                                toast.error('Failed to update visibility');
                              }
                            }}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] cursor-pointer"
                          />
                          <span className="text-[11px] text-[#374151] font-medium">domain.com.au</span>
                        </div>
                        <div className="flex items-center space-x-8">
                          <span className="text-[11px] text-[#374151] w-24 text-left opacity-70">{property?.domain_listing_id || '-'}</span>
                          <div className="w-40 flex justify-start items-center space-x-2">
                            {property?.published_on_domain ? (
                              <span className="text-[9px] px-2.5 py-0.5 rounded-full font-bold flex items-center shadow-sm whitespace-nowrap bg-cyan-100 text-[#55C5D0]">Published</span>
                            ) : (
                              <span className="text-[11px] text-[#374151] opacity-50">-</span>
                            )}
                            {(() => {
                              {/* domain.com.au redirects a bare ad id to the full listing URL */}
                              const domainAdId = String(property?.domain_listing_id || '').replace(/\D/g, '');
                              return property?.published_on_domain && domainAdId ? (
                                <a
                                  href={(['commercial','business'].includes(String(property?.property_type || '').toLowerCase()) || String(property?.property_class || '').toLowerCase() === 'commercial') ? `https://www.commercialrealestate.com.au/property/${domainAdId}` : `https://www.domain.com.au/${domainAdId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Open the live Domain listing"
                                  className="inline-flex items-center gap-1 text-[10px] font-bold text-[#55C5D0] hover:underline whitespace-nowrap"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  View
                                </a>
                              ) : null;
                            })()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between group">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={property?.published_on_collings || false}
                            onChange={async (e) => {
                              try {
                                await updateVisibility({ published_on_collings: e.target.checked });
                                setProperty(prev => ({ ...prev, published_on_collings: e.target.checked }));
                                toast.success(e.target.checked ? 'Published to Collings.com.au' : 'Unpublished from Collings.com.au');
                              } catch (err) {
                                toast.error('Failed to update visibility');
                              }
                            }}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] cursor-pointer"
                          />
                          <span className="text-[11px] text-[#374151] font-medium">Collings.com.au</span>
                        </div>
                        <div className="w-40 flex justify-start items-center space-x-2">
                          {property?.published_on_collings ? (
                            <span className="text-[9px] px-2.5 py-0.5 rounded-full font-bold flex items-center shadow-sm whitespace-nowrap bg-cyan-100 text-[#55C5D0]">Published</span>
                          ) : (
                            <span className="text-[11px] text-[#374151] opacity-50">-</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
                <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#374151] mb-2">Campaign Representatives</h3>
                <p className="text-[11px] text-[#374151] opacity-70 mb-10 font-medium leading-relaxed">Select which agents are displayed as the official contacts for this campaign.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-[11px] text-[#374151] mb-2 font-medium">Primary Agent</label>
                    <div className="relative group">
                      <select
                        className="w-full text-xs font-medium border border-gray-100 rounded-lg px-4 py-3 appearance-none bg-white focus:border-[#55C5D0] focus:ring-1 focus:ring-[#55C5D0] transition-all cursor-pointer"
                        value={property?.agent?.id || ''}
                        onChange={async (e) => {
                          const agentId = e.target.value;
                          try {
                            if (property?.id?.toString().startsWith('crm-')) {
                              await patchPublicProperty(property.id, { agent: agentId });
                            } else {
                              await updateCollingsProperty(property.id, { agent_id: agentId });
                            }
                            const updatedAgent = agents.find(a => String(a.id) === String(agentId)) || null;
                            setProperty(prev => ({ ...prev, agent: updatedAgent }));
                            toast.success("Primary agent updated");
                          } catch (err) {
                            toast.error("Failed to update agent");
                          }
                        }}
                        disabled={agentsLoading}
                      >
                        <option value="">Select Agent</option>
                        {agents.map((agent) => {
                          const displayName = agent.name || `${agent.first_name || ''} ${agent.last_name || ''}`.trim() || agent.email || 'Unknown Agent';
                          return (
                            <option key={agent.id} value={agent.id}>
                              {displayName}
                            </option>
                          );
                        })}
                      </select>
                      <ChevronDown className="w-4 h-4 text-gray-300 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none group-hover:text-[#55C5D0]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] text-[#374151] mb-2 font-medium">Secondary Agent (Optional)</label>
                    <div className="relative group">
                      <select
                        className="w-full text-xs font-medium border border-gray-100 rounded-lg px-4 py-3 appearance-none bg-white focus:border-[#55C5D0] focus:ring-1 focus:ring-[#55C5D0] transition-all cursor-pointer"
                        value={secondAgentId}
                        onChange={async (e) => {
                          const agentId = e.target.value;
                          setSecondAgentId(agentId || '');
                          setMakeSecondaryAgentOnListing(!!agentId);
                          const coAgentValue = agentId || '';
                          try {
                            if (property?.id?.toString().startsWith('crm-')) {
                              await patchPublicProperty(property.id, { co_agent: coAgentValue });
                            } else {
                              await patchCollingsProperty(property.id, { co_agent_id: coAgentValue });
                            }
                            const updatedCoAgent = coAgentValue ? (agents.find(a => String(a.id) === String(coAgentValue)) || null) : null;
                            setProperty(prev => ({ ...prev, co_agent: updatedCoAgent }));
                            toast.success(coAgentValue ? "Secondary agent updated" : "Secondary agent removed");
                          } catch (err) {
                            toast.error("Failed to update agent");
                          }
                        }}
                        disabled={agentsLoading}
                      >
                        <option value="">None</option>
                        {agents.map((agent) => {
                          const displayName = agent.name || `${agent.first_name || ''} ${agent.last_name || ''}`.trim() || agent.email || 'Unknown Agent';
                          return (
                            <option key={agent.id} value={String(agent.id)}>
                              {displayName}
                            </option>
                          );
                        })}
                      </select>
                      <ChevronDown className="w-4 h-4 text-gray-300 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none group-hover:text-[#55C5D0]" />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
                      This agent will have access to the listing but will not be shown on marketing.
                    </p>
                    <label className="block text-[11px] text-[#374151] mb-2 font-medium">Third Agent (Optional)</label>
                    <div className="relative group max-w-md">
                      <select
                        className="w-full text-xs font-medium border border-gray-100 rounded-lg px-4 py-3 appearance-none bg-white focus:border-[#55C5D0] focus:ring-1 focus:ring-[#55C5D0] transition-all cursor-pointer"
                        value={String(property?.tertiary_agent?.id ?? property?.extra_info?.tertiary_agent?.id ?? '')}
                        onChange={async (e) => {
                          const agentId = e.target.value;
                          const value = agentId === '' ? '' : String(agentId);
                          try {
                            if (property?.id?.toString().startsWith('crm-')) {
                              await patchPublicProperty(property.id, { tertiary_agent: value });
                            } else {
                              await patchCollingsProperty(property.id, { tertiary_agent_id: value });
                            }
                            const updatedTertiary = value ? (agents.find(a => String(a.id) === value) || null) : null;
                            setProperty(prev => {
                              const next = { ...prev, tertiary_agent: updatedTertiary };
                              if (prev.extra_info && !prev.id?.toString().startsWith('crm-')) {
                                next.extra_info = {
                                  ...prev.extra_info,
                                  tertiary_agent: updatedTertiary ? { id: updatedTertiary.id, name: updatedTertiary.name, email: updatedTertiary.email } : null,
                                  tertiary_agent_id: value || null
                                };
                              }
                              return next;
                            });
                            toast.success(value ? "Third agent updated" : "Third agent cleared");
                          } catch (err) {
                            toast.error("Failed to update agent");
                          }
                        }}
                        disabled={agentsLoading}
                      >
                        <option value="">None</option>
                        {agents.map((agent) => {
                          const displayName = agent.name || `${agent.first_name || ''} ${agent.last_name || ''}`.trim() || agent.email || 'Unknown Agent';
                          return (
                            <option key={agent.id} value={String(agent.id)}>
                              {displayName}
                            </option>
                          );
                        })}
                      </select>
                      <ChevronDown className="w-4 h-4 text-gray-300 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none group-hover:text-[#55C5D0]" />
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-[#374151] opacity-60 mb-10 font-medium">Selected agents (Primary and, when ticked, Secondary) will appear on buyer-facing listings and enquiry forms.</p>

                {/* Selected primary agent full details (name, email, phone) - like buyer portal */}
                {(() => {
                  const primaryAgent = agents.find(a => a.id === property?.agent_id || a.id === property?.agent?.id) || property?.agent;
                  if (!primaryAgent) return null;
                  const displayName = primaryAgent.name || `${primaryAgent.first_name || ''} ${primaryAgent.last_name || ''}`.trim() || primaryAgent.email || 'Property Agent';
                  const phone = primaryAgent.phone || primaryAgent.mobile || primaryAgent.mobile_number || primaryAgent.phone_number;
                  return (
                    <div className="mt-6 p-4 sm:p-5 rounded-xl border border-gray-100 bg-gray-50/50">
                      <h3 className="text-[12.93px] font-sans font-medium text-[#374151] mb-3 sm:mb-4">Property Agent (Primary)</h3>
                      <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                        {primaryAgent.photo ? (
                          <img src={primaryAgent.photo} alt={displayName} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-[#55C5D0]/20" />
                        ) : (
                          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#55C5D0]/10 flex items-center justify-center text-[#55C5D0] text-base sm:text-lg font-bold">
                            {displayName.charAt(0) || 'A'}
                          </div>
                        )}
                        <div>
                          <p className="text-[13px] sm:text-[14px] font-bold text-[#374151]">{displayName}</p>
                          {(primaryAgent.title || primaryAgent.role) && (
                            <p className="text-[11px] sm:text-[12px] text-[#6B7280]">{primaryAgent.title || primaryAgent.role}</p>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2 sm:space-y-2.5">
                        {primaryAgent.email && (
                          <a href={`mailto:${primaryAgent.email}`} className="flex items-center gap-2 sm:gap-2.5 text-[#374151] hover:text-[#55C5D0] transition-colors group">
                            <div className="p-1.5 bg-white rounded-lg border border-gray-100 group-hover:border-[#55C5D0]/30 transition-colors">
                              <Mail className="w-4 h-4 text-[#55C5D0]" />
                            </div>
                            <span className="text-[11px] sm:text-[12px] font-medium">{primaryAgent.email}</span>
                          </a>
                        )}
                        {phone && (
                          <a href={`tel:${phone}`} className="flex items-center gap-2 sm:gap-2.5 text-[#374151] hover:text-[#55C5D0] transition-colors group">
                            <div className="p-1.5 bg-white rounded-lg border border-gray-100 group-hover:border-[#55C5D0]/30 transition-colors">
                              <Phone className="w-4 h-4 text-[#55C5D0]" />
                            </div>
                            <span className="text-[11px] sm:text-[12px] font-medium">{phone}</span>
                          </a>
                        )}
                        {!primaryAgent.email && !phone && (
                          <p className="text-[11px] text-[#6B7280] font-medium">No contact details on file for this agent.</p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Selected secondary agent full details when set */}
                {(() => {
                  const coAgentId = property?.co_agent?.id ?? property?.co_agent;
                  const secondaryAgent = typeof coAgentId !== 'undefined' && coAgentId
                    ? (agents.find(a => a.id === coAgentId) || property?.co_agent)
                    : null;
                  if (!secondaryAgent) return null;
                  const displayName = secondaryAgent.name || `${secondaryAgent.first_name || ''} ${secondaryAgent.last_name || ''}`.trim() || secondaryAgent.email || 'Secondary Agent';
                  const phone = secondaryAgent.phone || secondaryAgent.mobile || secondaryAgent.mobile_number || secondaryAgent.phone_number;
                  return (
                    <div className="mt-4 p-4 sm:p-5 rounded-xl border border-gray-100 bg-gray-50/50">
                      <h3 className="text-[12.93px] font-sans font-medium text-[#374151] mb-3 sm:mb-4">Property Agent (Secondary)</h3>
                      <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                        {secondaryAgent.photo ? (
                          <img src={secondaryAgent.photo} alt={displayName} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-[#55C5D0]/20" />
                        ) : (
                          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#55C5D0]/10 flex items-center justify-center text-[#55C5D0] text-base sm:text-lg font-bold">
                            {displayName.charAt(0) || 'A'}
                          </div>
                        )}
                        <div>
                          <p className="text-[13px] sm:text-[14px] font-bold text-[#374151]">{displayName}</p>
                          {(secondaryAgent.title || secondaryAgent.role) && (
                            <p className="text-[11px] sm:text-[12px] text-[#6B7280]">{secondaryAgent.title || secondaryAgent.role}</p>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2 sm:space-y-2.5">
                        {secondaryAgent.email && (
                          <a href={`mailto:${secondaryAgent.email}`} className="flex items-center gap-2 sm:gap-2.5 text-[#374151] hover:text-[#55C5D0] transition-colors group">
                            <div className="p-1.5 bg-white rounded-lg border border-gray-100 group-hover:border-[#55C5D0]/30 transition-colors">
                              <Mail className="w-4 h-4 text-[#55C5D0]" />
                            </div>
                            <span className="text-[11px] sm:text-[12px] font-medium">{secondaryAgent.email}</span>
                          </a>
                        )}
                        {phone && (
                          <a href={`tel:${phone}`} className="flex items-center gap-2 sm:gap-2.5 text-[#374151] hover:text-[#55C5D0] transition-colors group">
                            <div className="p-1.5 bg-white rounded-lg border border-gray-100 group-hover:border-[#55C5D0]/30 transition-colors">
                              <Phone className="w-4 h-4 text-[#55C5D0]" />
                            </div>
                            <span className="text-[11px] sm:text-[12px] font-medium">{phone}</span>
                          </a>
                        )}
                        {!secondaryAgent.email && !phone && (
                          <p className="text-[11px] text-[#6B7280] font-medium">No contact details on file for this agent.</p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Third agent (access only, not on marketing) */}
                {(() => {
                  const tertId = property?.tertiary_agent?.id ?? property?.tertiary_agent ?? property?.extra_info?.tertiary_agent?.id;
                  const tertiaryAgent = typeof tertId !== 'undefined' && tertId
                    ? (agents.find(a => a.id === tertId) || property?.tertiary_agent || property?.extra_info?.tertiary_agent)
                    : null;
                  if (!tertiaryAgent) return null;
                  const displayName = tertiaryAgent.name || `${tertiaryAgent.first_name || ''} ${tertiaryAgent.last_name || ''}`.trim() || tertiaryAgent.email || 'Third Agent';
                  const phone = tertiaryAgent.phone || tertiaryAgent.mobile || tertiaryAgent.mobile_number || tertiaryAgent.phone_number;
                  return (
                    <div className="mt-4 p-4 sm:p-5 rounded-xl border border-gray-100 bg-amber-50/50">
                      <h3 className="text-[12.93px] font-sans font-medium text-[#374151] mb-1">Third Agent (access only, not on marketing)</h3>
                      <p className="text-[10px] text-amber-700 mb-3">This agent has access to the listing but is not shown on marketing.</p>
                      <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                        {tertiaryAgent.photo ? (
                          <img src={tertiaryAgent.photo} alt={displayName} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-[#55C5D0]/20" />
                        ) : (
                          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#55C5D0]/10 flex items-center justify-center text-[#55C5D0] text-base sm:text-lg font-bold">
                            {displayName.charAt(0) || 'A'}
                          </div>
                        )}
                        <div>
                          <p className="text-[13px] sm:text-[14px] font-bold text-[#374151]">{displayName}</p>
                          {(tertiaryAgent.title || tertiaryAgent.role) && (
                            <p className="text-[11px] sm:text-[12px] text-[#6B7280]">{tertiaryAgent.title || tertiaryAgent.role}</p>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2 sm:space-y-2.5">
                        {tertiaryAgent.email && (
                          <a href={`mailto:${tertiaryAgent.email}`} className="flex items-center gap-2 sm:gap-2.5 text-[#374151] hover:text-[#55C5D0] transition-colors group">
                            <Mail className="w-4 h-4 text-[#55C5D0]" />
                            <span className="text-[11px] sm:text-[12px] font-medium">{tertiaryAgent.email}</span>
                          </a>
                        )}
                        {phone && (
                          <a href={`tel:${phone}`} className="flex items-center gap-2 sm:gap-2.5 text-[#374151] hover:text-[#55C5D0] transition-colors group">
                            <Phone className="w-4 h-4 text-[#55C5D0]" />
                            <span className="text-[11px] sm:text-[12px] font-medium">{phone}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#374151] mb-4 mt-8">Internal Notes (Optional)</h3>
                <textarea
                  className="w-full text-[11px] border border-gray-100 rounded-xl p-4 min-h-[80px] bg-white hover:border-gray-200 focus:border-[#55C5D0] transition-all font-medium placeholder:text-[#374151]/50 placeholder:text-[11px] outline-none resize-y"
                  placeholder="Add internal notes about the Property Price Statement or campaign.."
                />
              </div>
            </div>

            {/* Property Price Statement is a sale-only compliance document; not applicable to rentals. */}
            {!isLease && (
            <div className="bg-white rounded-xl border border-gray-100 p-4 sm:p-6 md:p-8 shadow-sm flex flex-col w-full max-w-full">
              <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#374151] mb-2 tracking-tight">Property Price Statement</h3>
              <p className="text-[11px] text-[#374151] opacity-70 mb-6 sm:mb-10 font-medium leading-relaxed">Official Property Price Statement (PPS) for this property, used for compliance and buyer disclosure.</p>

              <div className="mb-8 sm:mb-12">
                <h4 className="text-[12px] font-bold text-[#374151] mb-4 sm:mb-6 font-sans">Property Price Statement (PDF)</h4>
                {(() => {
                  const soiDoc = sortedSoiDocs[0];
                  const previousSoi = sortedSoiDocs.slice(1);
                  if (soiDoc) {
                    return (
                      <div className="bg-[#F8F9FB] rounded-xl p-4 sm:p-6 md:p-8 border border-gray-100/50">
                        <div className="flex flex-col gap-4">
                          <div className="space-y-3 sm:space-y-4 min-w-0 flex-1 order-1">
                            <div>
                              <span className="text-[11px] text-[#374151] font-bold">Current Statement</span>
                            </div>
                            <div className="space-y-2">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-[11px] min-w-0">
                                <span className="text-[#374151] font-bold flex-shrink-0">File:</span>
                                <span className="text-[#374151] opacity-60 font-medium break-words [overflow-wrap:anywhere]" title={soiDoc.name}>{soiDoc.name || 'Property_Price_Statement.pdf'}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-[11px]">
                                <span className="text-[#374151] font-bold whitespace-nowrap">Last Updated:</span>
                                <span className="text-[#374151] opacity-60 font-medium whitespace-nowrap">{soiDoc.uploaded_at ? new Date(soiDoc.uploaded_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-[11px]">
                                <span className="text-[#374151] font-bold whitespace-nowrap">Uploaded by:</span>
                                <span className="text-[#55C5D0] font-medium underline underline-offset-2 break-words min-w-0 cursor-pointer">{property?.agent?.name || 'Agent'}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-row flex-wrap gap-[clamp(0.25rem,1vw,0.5rem)] w-full flex-shrink-0 order-2">
                            <a href={soiDoc.file} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center flex-1 min-w-[clamp(80px,22vw,140px)] md:min-w-0 md:w-full py-[clamp(0.5rem,1.2vh,0.65rem)] px-[clamp(0.75rem,2vw,1rem)] bg-[#E8F5E9] text-[#2E7D32] rounded-lg text-[clamp(9px,2vw,11px)] font-bold tracking-tight border border-[#C8E6C9] hover:bg-[#C8E6C9] transition-all text-center whitespace-nowrap">
                              Download
                            </a>
                            <button type="button" onClick={handleReplaceSOI} disabled={soiUploading} className="inline-flex items-center justify-center flex-1 min-w-[clamp(70px,18vw,120px)] md:min-w-0 md:w-full py-[clamp(0.5rem,1.2vh,0.65rem)] px-[clamp(0.75rem,2vw,1rem)] bg-[#E3F2FD] text-[#1976D2] rounded-lg text-[clamp(9px,2vw,11px)] font-bold tracking-tight border border-[#BBDEFB] hover:bg-[#BBDEFB] transition-all disabled:opacity-50 whitespace-nowrap">
                              Replace
                            </button>
                            <button type="button" onClick={handleRemoveSOI} disabled={soiUploading} className="inline-flex items-center justify-center flex-1 min-w-[clamp(70px,18vw,120px)] md:min-w-0 md:w-full py-[clamp(0.5rem,1.2vh,0.65rem)] px-[clamp(0.75rem,2vw,1rem)] bg-[#FFEBEE] text-[#D32F2F] rounded-lg text-[clamp(9px,2vw,11px)] font-bold tracking-tight border border-[#FFCDD2] hover:bg-[#FFCDD2] transition-all disabled:opacity-50 whitespace-nowrap">
                              Remove
                            </button>
                          </div>
                          {previousSoi.length > 0 && (
                            <div className="w-full order-3 pt-3 mt-1 border-t border-gray-200/70 space-y-2">
                              <span className="text-[11px] text-[#374151] font-bold">Previous versions</span>
                              {previousSoi.map((doc) => (
                                <div key={doc.id} className="flex items-center justify-between gap-2 text-[11px] min-w-0">
                                  <span className="text-[#374151] opacity-60 font-medium break-words [overflow-wrap:anywhere] min-w-0" title={doc.name}>{doc.name || 'Property_Price_Statement.pdf'}</span>
                                  <span className="text-[#374151] opacity-50 whitespace-nowrap flex-shrink-0">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                                  <a href={doc.file} target="_blank" rel="noopener noreferrer" className="text-[#55C5D0] font-bold whitespace-nowrap flex-shrink-0 hover:underline">Download</a>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div className="bg-[#F8F9FB] rounded-xl p-6 sm:p-8 border border-gray-100/50 text-center">
                        <p className="text-[11px] text-[#374151] opacity-60">No Property Price Statement uploaded yet.</p>
                      </div>
                    );
                  }
                })()}
              </div>

              {(() => {
                const soiDoc = sortedSoiDocs[0];
                // Only show drag-and-drop upload area when no document exists
                if (!soiDoc) {
                  return (
                    <div>
                      <h4 className="text-[12px] font-bold text-[#374151] mb-4 sm:mb-6 font-sans">Upload New Property Price Statement</h4>
                      <input
                        ref={soiFileInputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleSOIUpload([file]);
                        }}
                      />
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => !soiUploading && soiFileInputRef.current?.click()}
                        onKeyDown={(e) => e.key === "Enter" && !soiUploading && soiFileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (soiUploading) return;
                          const file = e.dataTransfer?.files?.[0];
                          if (file) handleSOIUpload([file]);
                        }}
                        className="border border-dashed border-gray-200 rounded-2xl p-8 sm:p-12 md:p-16 flex flex-col items-center justify-center text-center hover:bg-gray-50 hover:border-[#55C5D0]/30 transition-all cursor-pointer group bg-white touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <div className="w-16 h-12 mb-6">
                          <svg viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-[#55C5D0]">
                            <path d="M48 16C46.5 16 45.1 16.3 43.8 16.9C42.2 10.6 36.6 6 30 6C23.4 6 17.8 10.6 16.2 16.9C14.9 16.3 13.5 16 12 16C5.4 16 0 21.4 0 28C0 34.6 5.4 40 12 40H48C54.6 40 60 34.6 60 28C60 21.4 54.6 16 48 16Z" fill="currentColor" />
                            <path d="M30 18L22 26H28V34H32V26H38L30 18Z" fill="white" />
                          </svg>
                        </div>
                        <div className="space-y-2">
                          {soiUploading ? (
                            <p className="text-[11.5px] font-medium text-[#374151]">Uploading...</p>
                          ) : (
                            <>
                              <p className="text-[11.5px] font-medium text-[#374151]">
                                <span className="font-extrabold">Drag & drop</span> Property Price Statement
                              </p>
                              <p className="text-[11.5px] text-[#374151] opacity-70 font-medium">
                                PDF here or <span className="font-extrabold">click</span> to upload
                              </p>
                              <p className="text-[10px] text-[#374151] opacity-40 font-medium pt-1">Accepted format: PDF - Max file size: 2MB</p>
                            </>
                          )}
                        </div>
                      </div>
                      <p className="text-[10px] text-[#374151] opacity-60 mt-8 font-medium leading-relaxed">This document is required before the property can be publicly shared.</p>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-10 shadow-sm mt-6">
            <div className="flex items-center justify-between mb-10">
              <div className="flex items-center space-x-4">
                <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#374151]">About the Property</h3>
              </div>
              {isEditingDescription ? (
                <div className="flex space-x-2">
                  <button onClick={() => setIsEditingDescription(false)} className="px-5 py-2 bg-[#ECECEC] text-[#374151] rounded-xl text-xs font-black tracking-widest transition-all uppercase">Cancel</button>
                  <button onClick={handleSaveDescription} disabled={isUpdating} className="px-5 py-2 bg-[#55C5D0] text-white rounded-xl text-xs font-black tracking-widest transition-all uppercase disabled:opacity-50">
                    {isUpdating ? "Saving..." : "Save Description"}
                  </button>
                </div>
              ) : (
                <button onClick={handleEditDescription} className="px-5 py-2 bg-[#ECECEC] text-[#374151] rounded-xl text-xs font-black tracking-widest transition-all uppercase">Edit Description</button>
              )}
            </div>

            {isEditingDescription ? (
              <textarea
                value={descriptionEdit}
                onChange={(e) => setDescriptionEdit(e.target.value)}
                className="w-full min-h-[400px] p-6 text-sm text-[#374151] font-medium border border-gray-100 rounded-xl focus:border-[#55C5D0] outline-none leading-relaxed placeholder:text-[#374151]/40"
                placeholder="Write a compelling property description..."
              />
            ) : (
              <div className="text-[#374151] text-[13px] leading-[1.8] font-medium space-y-8 max-w-full">
                {formatDescription(property.description).map((paragraph, index) => (
                  <p key={index} className="opacity-90">{paragraph}</p>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#374151]">Property Information</h3>
              {isEditingInfo ? (
                <div className="flex space-x-2">
                  <button onClick={() => setIsEditingInfo(false)} className="px-3 py-1 bg-[#ECECEC] text-[#374151] rounded-md text-[10px] font-bold transition-all">Cancel</button>
                  <button onClick={handleSaveInfo} disabled={isUpdating} className="px-3 py-1 bg-[#55C5D0] text-white rounded-md text-[10px] font-bold transition-all disabled:opacity-50">
                    {isUpdating ? "Saving..." : "Save"}
                  </button>
                </div>
              ) : (
                <button onClick={handleEditInfo} className="px-3 py-1 bg-[#ECECEC] text-[#374151] rounded-md text-[10px] font-bold transition-all">Edit</button>
              )}
            </div>

            {isEditingInfo ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-sans font-normal text-[11.5px] leading-[17.24px] text-[#374151]">Price</span>
                  <input
                    type="text"
                    value={editForm.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    className="w-24 text-right text-xs font-bold border border-gray-200 rounded px-2 py-1 outline-none focus:border-[#55C5D0] text-[#374151]"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-sans font-normal text-[11.5px] leading-[17.24px] text-[#374151]">Weekly Rent</span>
                  <input
                    type="text"
                    value={editForm.weekly_rent ?? ""}
                    onChange={(e) => handleInputChange('weekly_rent', e.target.value)}
                    placeholder="Optional"
                    className="w-24 text-right text-xs font-bold border border-gray-200 rounded px-2 py-1 outline-none focus:border-[#55C5D0] text-[#374151]"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-sans font-normal text-[11.5px] leading-[17.24px] text-[#374151]">Type</span>
                  <select
                    value={editForm.property_type}
                    onChange={(e) => handleInputChange('property_type', e.target.value)}
                    className="w-32 text-right text-xs font-bold border border-gray-100 rounded bg-gray-50 px-2 py-1 outline-none text-[#374151]"
                  >
                    <option value="Residential">residential</option>
                    <option value="Commercial">commercial</option>
                    <option value="Land">land</option>
                  </select>
                </div>
                {[
                  { field: 'beds', label: 'Bedrooms' },
                  { field: 'baths', label: 'Bathrooms' },
                  { field: 'garages', label: 'Garage' },
                  { field: 'car_ports', label: 'Carports' },
                  { field: 'open_spaces', label: 'Open parking' },
                ].map((fieldOrConfig) => {
                  const field = fieldOrConfig.field;
                  const label = fieldOrConfig.label;
                  return (
                  <div key={field} className="flex items-center justify-between">
                    <span className="font-sans font-normal text-[11.5px] leading-[17.24px] text-[#374151]">
                      {label}
                    </span>
                    <div className="flex items-center space-x-1 border border-gray-100 rounded-md bg-gray-50 overflow-hidden">
                      <button onClick={() => handleInputChange(field, Math.max(0, (editForm[field] || 0) - 1))} className="px-2 py-1 hover:bg-gray-200 text-[#374151] font-bold">-</button>
                      <span className="w-8 text-center text-xs font-bold text-[#374151]">{editForm[field] || 0}</span>
                      <button onClick={() => handleInputChange(field, (editForm[field] || 0) + 1)} className="px-2 py-1 hover:bg-gray-200 text-[#374151] font-bold">+</button>
                    </div>
                  </div>
                  );
                })}
                <div className="flex items-center justify-between">
                  <span className="font-sans font-normal text-[11.5px] leading-[17.24px] text-[#374151]">Floor Area (m²)</span>
                  <div className="relative">
                    <input
                      type="number"
                      value={editForm.floor_area}
                      onChange={(e) => handleInputChange('floor_area', e.target.value)}
                      className="w-20 text-right text-xs font-bold border border-gray-200 rounded px-2 py-1 outline-none focus:border-[#55C5D0] pr-6 text-[#374151]"
                    />
                    <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-[#374151] opacity-60 font-bold">m²</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-sans font-normal text-[11.5px] leading-[17.24px] text-[#374151]">Land Area (m²)</span>
                  <div className="relative">
                    <input
                      type="number"
                      value={editForm.land_area}
                      onChange={(e) => handleInputChange('land_area', e.target.value)}
                      className="w-20 text-right text-xs font-bold border border-gray-200 rounded px-2 py-1 outline-none focus:border-[#55C5D0] pr-6 text-[#374151]"
                    />
                    <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-[#374151] opacity-60 font-bold">m²</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                {[
                  { label: "Price", value: formatPriceDisplay(property), isPrice: true },
                  ...(property?.weekly_rent != null && property.weekly_rent !== '' ? [{ label: "Weekly Rent", value: formatPrice(property.weekly_rent), isPrice: true }] : []),
                  { label: "Type", value: String(property.property_type || 'residential').toLowerCase(), isBadge: true },
                  { label: "Bedrooms", value: property.beds ?? 0 },
                  { label: "Bathrooms", value: property.baths ?? 0 },
                  { label: "Garage", value: property.garages ?? property.parkings ?? property.parking_spaces ?? 0 },
                  { label: "Carports", value: property.car_ports ?? 0 },
                  { label: "Open parking", value: property.open_spaces ?? property.open_space ?? 0 },
                  { label: "Floor Area (m²)", value: property.floor_area || 0 },
                  { label: "Land Area (m²)", value: property.land_area || "N/A" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <span className="font-sans font-normal text-[11.5px] leading-[17.24px] text-[#374151] opacity-70">
                      {item.label}
                    </span>
                    {item.isBadge ? (
                      <span className="text-[11px] font-semibold bg-[#E0F2FF] text-[#0066CC] px-2.5 py-0.5 rounded-full lowercase">{item.value}</span>
                    ) : item.isPrice ? (
                      <span className="text-lg font-bold text-[#55C5D0]">{item.value}</span>
                    ) : item.isPriceText ? (
                      <span className="text-[11px] font-medium text-gray-500 italic">{item.value}</span>
                    ) : (
                      <span className="font-sans font-bold text-[12.93px] leading-[18.75px] text-[#374151]">{item.value}{item.label.includes("Area") ? "m²" : ""}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
            <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#374151] mb-8">Listing Details</h3>
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <span className="font-sans font-normal text-[11.5px] leading-[17.24px] text-[#374151]">Listing ID</span>
                <span className="text-xs font-medium text-[#374151]">{params.id.slice(-8).toUpperCase()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-sans font-normal text-[11.5px] leading-[17.24px] text-[#374151]">Created</span>
                <span className="text-xs font-medium text-[#374151]">{formatDate(property.created_at)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-sans font-normal text-[11.5px] leading-[17.24px] text-[#374151]">Status</span>
                <span className="text-[10px] px-3 py-1 bg-[#E1F5FE] text-[#039BE5] rounded-full font-bold">Active</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
            <h3 className="font-sans font-medium text-[12.93px] leading-[18.75px] text-[#374151] mb-8">Location</h3>
            <div className="flex items-start space-x-3">
              <div className="mt-0.5">
                <MapPin className="w-4 h-4 text-[#374151] opacity-40" />
              </div>
              <div className="space-y-1">
                <p className="text-xs font-bold text-[#374151]">{property.address || "405 High Street"}</p>
                <p className="text-[11px] text-[#374151] opacity-70 font-normal leading-relaxed">{property.address}, Northcote, VIC, 3070</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col bg-[#F8FAFC] min-h-screen -mx-[14px] -my-3 sm:-mx-4 sm:-my-4 md:-m-6">
      {/* Sticky only on md+ — on mobile the action button grid makes the header
          too tall, so it pins over and clips the tab nav underneath. */}
      <div className="bg-white border-b border-gray-100 md:sticky md:top-0 md:z-40 shadow-sm">
        <div className="w-full px-4 sm:px-6 md:px-8 py-4 sm:py-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-start sm:items-center gap-3 sm:gap-4 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="w-10 h-10 flex-shrink-0 bg-white border border-gray-200 rounded-xl flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
              >
                <ArrowLeft className="w-5 h-5 text-[#374151]" />
              </button>
              <div className="flex flex-col space-y-1 min-w-0 flex-1">
                <div className="flex items-center space-x-3 mb-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 text-white text-[10px] font-bold rounded-md leading-tight flex-shrink-0 ${(property?.status?.name || property?.status || '').toLowerCase().includes('off')
                    ? 'bg-orange-500'
                    : (property?.status?.name || property?.status || '').toLowerCase().includes('sold')
                      ? 'bg-blue-500'
                      : (property?.status?.name || property?.status || '').toLowerCase().includes('prospect')
                        ? 'bg-yellow-500'
                        : 'bg-[#4CAF50]' // Green for Active/On Market
                    }`}>
                    {property?.status?.name || property?.status || 'Active'}
                  </span>
                </div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-[#374151] tracking-tighter leading-tight break-words">
                  {property.address}
                </h1>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-4 flex-shrink-0">
              <button
                onClick={() => navigate(`/public-property-search/edit/${params.id}`)}
                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#2D3139] hover:bg-black text-white rounded-xl text-[10px] font-black transition-all shadow-xl shadow-black/5 active:scale-95 uppercase tracking-widest flex-1 sm:flex-none min-w-0"
              >
                <Edit className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">EDIT PROPERTY</span>
              </button>
              <PropertyWithdrawControls property={property} onWithdraw={handlePropertyWithdraw} onRelist={handlePropertyRelist}>
                {({ onClick, loading }) => (
                  <button
                    onClick={onClick}
                    disabled={loading}
                    className={`flex items-center justify-center space-x-2 px-4 sm:px-5 py-2.5 rounded-lg text-[10px] sm:text-xs font-bold transition-all shadow-sm border flex-1 sm:flex-none min-w-0 ${property?.is_withdrawn
                      ? "bg-[#E0F2F1] hover:bg-teal-50 text-teal-600 border-teal-100"
                      : "bg-[#FFEBEE] hover:bg-[#FFCDD2] text-[#D32F2F] border-[#FFCDD2]"
                      } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <PauseCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{property?.is_withdrawn ? 'Relist' : 'Withdraw'}</span>
                  </button>
                )}
              </PropertyWithdrawControls>
              <button
                onClick={() => setShowSoldModal(true)}
                disabled={isMarkingSold}
                className={`flex items-center justify-center space-x-2 px-4 sm:px-5 py-2.5 rounded-lg text-[10px] sm:text-xs font-bold transition-all shadow-sm border flex-1 sm:flex-none min-w-0 ${(property?.is_sold || property?.status === 'Sold' || property?.status?.name === 'Sold')
                  ? "bg-[#E8F5E9] hover:bg-green-50 text-green-600 border-green-100"
                  : "bg-[#E3F2FD] hover:bg-blue-50 text-blue-600 border-blue-100"
                  } ${isMarkingSold ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{(property?.is_sold || property?.status === 'Sold' || property?.status?.name === 'Sold') ? 'Mark Unsold' : 'Mark as Sold'}</span>
              </button>

              {isPropertySold && (
                <div className="w-full sm:w-auto sm:min-w-[280px] flex-1">
                  <SoldThroughPortalToggle
                    checked={property?.sold_through_collings_portal}
                    onToggle={handlePortalAttributionToggle}
                  />
                </div>
              )}

              {(property?.is_sold || property?.status === 'Sold' || property?.status?.name === 'Sold') ? (
                <ConfirmModal
                  isOpen={showSoldModal}
                  onClose={() => setShowSoldModal(false)}
                  onConfirm={() => handleToggleSold()}
                  loading={isMarkingSold}
                  title="Mark Property as Unsold"
                  description="Are you sure you want to mark this property as unsold? It will be removed from sold properties and commission records will remain."
                  confirmText="Mark Unsold"
                  variant="primary"
                />
              ) : (
                <MarkSoldModal
                  isOpen={showSoldModal}
                  onClose={() => setShowSoldModal(false)}
                  onConfirm={handleToggleSold}
                  loading={isMarkingSold}
                  address={property?.address}
                  initialSoldPrice={property?.sold_price}
                  initialSuppress={property?.suppress_sold_price}
                  initialSoldThroughPortal={property?.sold_through_collings_portal}
                />
              )}

              {/* Delete button hidden per request */}
              <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteProperty}
                loading={isDeleting}
                title="Delete Property"
                description="Are you sure you want to permanently delete this property? This action cannot be undone and all associated data (enquiries, notes, documents) will be removed."
                confirmText="Delete Property"
                variant="danger"
              />

            </div>
          </div>
        </div>
      </div>

      <div className="w-full space-y-10 mt-6">
        <div className="bg-white border-b border-gray-200 px-2 sm:px-6 md:px-8 overflow-x-auto">
          <nav className="flex space-x-6 sm:space-x-12 min-w-max pb-px">
            {[
              { id: 'property', label: 'Property' },
              { id: 'owner', label: 'Owner' },
              { id: 'purchaser', label: 'Purchaser' },
              ...(canViewBuyerData
                ? [
                    { id: 'enquiries', label: `Enquiries (${enquiries.length + bidderRegistrations.length})` },
                    { id: 'offers', label: `Offers (${offers.length})` },
                  ]
                : []),
              { id: 'notes', label: `Agent Notes (${propertyNotes.length})` },
              { id: 'documents', label: 'Documents' },
              { id: 'buyer_match', label: `Buyer Match (${buyerMatches.length})` },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group py-6 px-1 border-b-4 font-black text-xs tracking-[0.15em] transition-all flex items-center uppercase ${activeTab === tab.id
                  ? 'border-[#55C5D0] text-[#55C5D0]'
                  : 'border-transparent text-[#374151] opacity-50 hover:opacity-100'
                  }`}
              >
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="pb-20 px-4 sm:px-6 md:px-8">
          {activeTab === 'property' && renderPropertyTab()}
          {activeTab === 'owner' && (
            <div className="animate-in zoom-in-95 duration-500 space-y-8">
              <div className="bg-white border border-gray-100 p-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-[#374151] mb-1">Property Owners</h3>
                  <p className="text-xs text-[#374151] opacity-60 font-medium">Manage property owners and their information</p>
                </div>
                <button
                  onClick={() => setShowOwnerModal(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#55C5D0] text-white rounded-lg text-xs font-bold uppercase tracking-wide shadow-lg shadow-cyan-100 hover:bg-[#4AADB7] transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Owner</span>
                </button>
              </div>
              <div className="space-y-4 px-8">
                {owners.map((owner, idx) => {
                  const ownerPhone = owner.contact || owner.phone;
                  const ownerEmail = owner.email?.trim() || '';
                  return (
                    <div key={owner.id || idx} className="bg-white border border-gray-100 rounded-xl p-6 flex items-center justify-between hover:border-[#55C5D0]/30 transition-all group shadow-sm">
                      <div className="flex items-center space-x-5">
                        <div className="w-12 h-12 bg-[#55C5D0] rounded-full flex items-center justify-center text-white shadow-sm shrink-0">
                          <User className="w-6 h-6" />
                        </div>
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-bold text-[#374151]">{owner.name}</p>
                            {owner.isPrimary && (
                              <span className="text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 bg-[#55C5D0]/10 text-[#55C5D0] rounded-full">Primary</span>
                            )}
                          </div>
                          {owner.preferred_name && (
                            <p className="text-[11px] text-[#374151] opacity-60 font-medium italic">
                              goes by {owner.preferred_name}
                            </p>
                          )}
                          <p className="text-[11px] text-[#374151] opacity-60 font-medium">{ownerEmail || 'No email'}</p>
                          <p className="text-[11px] text-[#374151] opacity-60 font-medium">{ownerPhone || 'No phone'}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {ownerPhone ? (
                          <a href={`tel:${ownerPhone.replace(/\s/g, '')}`} className="p-2 text-[#55C5D0] hover:bg-[#E0F7FA] rounded-full transition-colors">
                            <Phone className="w-4 h-4" />
                          </a>
                        ) : (
                          <span className="p-2 text-gray-300 cursor-not-allowed" title="No phone"><Phone className="w-4 h-4" /></span>
                        )}
                        {ownerEmail ? (
                          <a href={`mailto:${ownerEmail}`} className="p-2 text-[#55C5D0] hover:bg-[#E0F7FA] rounded-full transition-colors">
                            <Mail className="w-4 h-4" />
                          </a>
                        ) : (
                          <span className="p-2 text-gray-300 cursor-not-allowed" title="No email"><Mail className="w-4 h-4" /></span>
                        )}
                        <button
                          onClick={() => handleDeleteOwner(owner)}
                          className="p-2 text-red-400 hover:bg-red-50 rounded-full transition-colors"
                          title="Remove owner"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {owners.length === 0 && (
                  <div className="text-center py-16 border-2 border-dashed border-gray-100 rounded-xl">
                    <p className="text-sm text-[#374151] opacity-50 font-medium">No owners added yet. Click "Add Owner" to get started.</p>
                  </div>
                )}
              </div>
            </div>
          )}
          {activeTab === 'purchaser' && (
            <div className="animate-in zoom-in-95 duration-500 space-y-8">
              <div className="bg-white border border-gray-100 p-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-[#374151] mb-1">Property Purchaser</h3>
                  <p className="text-xs text-[#374151] opacity-60 font-medium">Manage property purchaser and their information</p>
                </div>
                <button
                  onClick={() => setShowPurchaserModal(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#55C5D0] text-white rounded-lg text-xs font-bold uppercase tracking-wide shadow-lg shadow-cyan-100 hover:bg-[#4AADB7] transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Purchaser</span>
                </button>
              </div>
              <div className="space-y-4 px-8">
                {purchasers.map((purchaser, idx) => {
                  const purchaserPhone = purchaser.contact || purchaser.phone;
                  const purchaserEmail = purchaser.email?.trim() || '';
                  return (
                    <div key={idx} className="bg-white border border-gray-100 rounded-xl p-6 flex items-center justify-between hover:border-[#55C5D0]/30 transition-all group shadow-sm">
                      <div className="flex items-center space-x-5">
                        <div className="w-12 h-12 bg-[#55C5D0] rounded-full flex items-center justify-center text-white shadow-sm shrink-0">
                          <User className="w-6 h-6" />
                        </div>
                        <div className="space-y-0.5">
                          <p className="text-sm font-bold text-[#374151]">{purchaser.name}</p>
                          <p className="text-[11px] text-[#374151] opacity-60 font-medium">{purchaserEmail || 'No email'}</p>
                          <p className="text-[11px] text-[#374151] opacity-60 font-medium">{purchaserPhone || 'No phone'}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {purchaserPhone ? (
                          <a href={`tel:${purchaserPhone.replace(/\s/g, '')}`} className="p-2 text-[#55C5D0] hover:bg-[#E0F7FA] rounded-full transition-colors">
                            <Phone className="w-4 h-4" />
                          </a>
                        ) : (
                          <span className="p-2 text-gray-300 cursor-not-allowed" title="No phone"><Phone className="w-4 h-4" /></span>
                        )}
                        {purchaserEmail ? (
                          <a href={`mailto:${purchaserEmail}`} className="p-2 text-[#55C5D0] hover:bg-[#E0F7FA] rounded-full transition-colors">
                            <Mail className="w-4 h-4" />
                          </a>
                        ) : (
                          <span className="p-2 text-gray-300 cursor-not-allowed" title="No email"><Mail className="w-4 h-4" /></span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {purchasers.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-gray-100 rounded-xl bg-gray-50/50">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4">
                      <Users2 className="w-8 h-8 text-[#55C5D0]" />
                    </div>
                    <h4 className="text-sm font-bold text-[#374151] mb-1">No Purchasers Yet</h4>
                    <p className="text-xs text-[#374151] opacity-50 font-medium mb-6 max-w-xs mx-auto">
                      Add a purchaser to track sales information and contract details for this property.
                    </p>
                    <button
                      onClick={() => setShowPurchaserModal(true)}
                      className="flex items-center gap-2 px-6 py-3 bg-[#55C5D0] text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-xl shadow-cyan-100 hover:bg-[#4AADB7] transition-all"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add First Purchaser</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          {activeTab === 'enquiries' && canViewBuyerData && (
            <div className="space-y-6">
              {interactionsLoading ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-20 flex flex-col items-center justify-center shadow-sm">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55C5D0]"></div>
                  <p className="mt-4 text-sm text-gray-500">Loading enquiries...</p>
                </div>
              ) : (enquiries.length === 0 && bidderRegistrations.length === 0) ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-32 flex flex-col items-center justify-center text-center shadow-sm">
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                    <Users2 className="w-8 h-8 text-[#374151] opacity-20" />
                  </div>
                  <h4 className="text-xl font-black text-[#374151] mb-2 uppercase tracking-widest">No Enquiries</h4>
                  <p className="text-[#374151] opacity-50 font-bold text-xs uppercase tracking-widest max-w-[250px]">No enquiries recorded for this property.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  {enquiries.length > 0 && (
                    <>
                      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-transparent">
                        <h3 className="text-sm font-bold text-[#374151] uppercase tracking-wider flex items-center gap-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          Enquiries ({enquiries.length})
                        </h3>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {enquiries.map((enq) => (
                          <div
                            key={enq.id}
                            onClick={() => setEditingEnquiry(enq)}
                            className="p-6 hover:bg-gray-50 transition-colors cursor-pointer group"
                          >
                            <div className="flex justify-between items-start">
                              <div className="space-y-2 flex-1">
                                <div className="flex items-center gap-3">
                                  <span className="text-base font-bold text-[#374151]">{enq.name || enq.full_name}</span>
                                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                                    {enq.type_display || 'Enquiry'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  <a
                                    href={`mailto:${enq.email}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex items-center gap-1 hover:text-[#55C5D0] transition-colors"
                                  >
                                    <Mail size={14} /> {enq.email}
                                  </a>
                                  {(enq.phone || enq.contact) && (
                                    <a
                                      href={`tel:${enq.phone || enq.contact}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex items-center gap-1 hover:text-[#55C5D0] transition-colors"
                                    >
                                      <Phone size={14} /> {enq.phone || enq.contact}
                                    </a>
                                  )}
                                </div>
                                <div className="relative">
                                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg pr-10">{enq.message}</p>
                                  {enq.agent_notes && (
                                    <div className="mt-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                                      <span className="text-[10px] font-bold text-amber-600 uppercase tracking-widest block mb-1">Internal agent notes:</span>
                                      <p className="text-xs text-amber-800 whitespace-pre-wrap">{enq.agent_notes}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="self-center p-2 rounded-full bg-gray-50 text-gray-300 group-hover:bg-[#55C5D0]/10 group-hover:text-[#55C5D0] transition-all">
                                <Eye size={18} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'offers' && canViewBuyerData && (
            <div className="space-y-6">
              {interactionsLoading ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-20 flex flex-col items-center justify-center shadow-sm">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55C5D0]"></div>
                  <p className="mt-4 text-sm text-gray-500">Loading offers...</p>
                </div>
              ) : offers.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-32 flex flex-col items-center justify-center text-center shadow-sm">
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                    <DollarSign className="w-8 h-8 text-[#374151] opacity-20" />
                  </div>
                  <h4 className="text-xl font-black text-[#374151] mb-2 uppercase tracking-widest">No Offers</h4>
                  <p className="text-[#374151] opacity-50 font-bold text-xs uppercase tracking-widest max-w-[250px]">No offers recorded for this property.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-transparent">
                    <h3 className="text-sm font-bold text-[#374151] uppercase tracking-wider flex items-center gap-2">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                      Property Offers ({offers.length})
                    </h3>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {offers.map((offer) => (
                      <div
                        key={offer.id}
                        className="p-8 hover:bg-[#f0fafb]/40 transition-colors cursor-pointer group"
                        onClick={() => setViewingOffer(offer)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="space-y-4 flex-1">
                            {/* Header: Name & Amount */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-[#55C5D0] flex items-center justify-center text-white font-black text-lg shadow-sm">
                                  {offer.buyer_name ? offer.buyer_name[0].toUpperCase() : '?'}
                                </div>
                                <div>
                                  <h4 className="text-lg font-black text-[#374151]">{offer.buyer_name}</h4>
                                  <p className="text-xs font-bold text-[#55C5D0] uppercase tracking-widest">{offer.reference_number}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-black text-[#55C5D0]">
                                  {offer.offer_amount ? `$${Number(offer.offer_amount).toLocaleString()}` :
                                    offer.offer_amount_min ? `$${Number(offer.offer_amount_min).toLocaleString()}` : 'N/A'}
                                </div>
                                <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md border ${offerStatusPillClasses(offer.status)}`}>
                                  {offer.status_display || offer.status}
                                </span>
                              </div>
                            </div>

                            {/* Contact & Settlement */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100 group-hover:bg-white transition-colors">
                              <div className="space-y-2">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Contact Information</span>
                                <div className="flex items-center gap-2 text-xs font-bold text-[#374151]">
                                  <Mail size={14} className="text-[#55C5D0]" /> {offer.buyer_email}
                                </div>
                                {offer.buyer_phone && (
                                  <div className="flex items-center gap-2 text-xs font-bold text-[#374151]">
                                    <Phone size={14} className="text-[#55C5D0]" /> {offer.buyer_phone}
                                  </div>
                                )}
                              </div>
                              <div className="space-y-2">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Settlement & Deposit</span>
                                <div className="flex items-center gap-2 text-xs font-bold text-[#374151]">
                                  <Calendar size={14} className="text-[#55C5D0]" /> {offer.custom_settlement_date ? moment(offer.custom_settlement_date).format('D MMM YYYY') : `${offer.settlement_days || 30} Days`}
                                  {offer.custom_settlement_date && <span className="opacity-50 ml-1">({offer.settlement_days} Days)</span>}
                                  {!offer.custom_settlement_date && !offer.settlement_days && <span className="opacity-50 ml-1">({offer.preferred_settlement_period || 'Standard'})</span>}
                                </div>
                                <div className="flex items-center gap-2 text-xs font-bold text-[#374151]">
                                  <div className="w-3.5 h-3.5 flex items-center justify-center text-[10px] font-black text-[#55C5D0] border border-[#55C5D0] rounded-full">$</div>
                                  Deposit: ${Number(offer.deposit_amount || 0).toLocaleString()}
                                </div>
                              </div>
                            </div>

                            {/* Conditions */}
                            {offer.conditions && offer.conditions.length > 0 && (
                              <div className="space-y-2">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Conditions</span>
                                <div className="flex flex-wrap gap-2">
                                  {offer.conditions.map((cond, i) => (
                                    <span key={i} className="px-3 py-1 bg-white border border-gray-200 rounded-lg text-xs font-bold text-[#374151] shadow-sm">
                                      {typeof cond === 'string' ? cond : (cond.type?.replace(/_/g, ' ') || 'Condition')}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Message */}
                            {offer.message && (
                              <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-100/50">
                                <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-1 block">Buyer Message:</span>
                                <p className="text-sm text-gray-700 italic">"{offer.message}"</p>
                              </div>
                            )}

                            {/* Agent Notes */}
                            {offer.agent_notes && (
                              <div className="bg-[#55C5D0]/5 p-4 rounded-xl border border-[#55C5D0]/10">
                                <span className="text-[10px] font-black text-[#55C5D0] uppercase tracking-widest mb-1 block">Internal Response / Notes:</span>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{offer.agent_notes}</p>
                              </div>
                            )}
                          </div>
                          <div className="self-center ml-4 p-2 rounded-full bg-gray-50 text-gray-300 group-hover:bg-[#55C5D0]/10 group-hover:text-[#55C5D0] transition-all">
                            <Eye size={20} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}





          {activeTab === 'notes' && (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left: Add Note Form */}
                <div className="space-y-4">
                  <h3 className="text-xl font-black text-[#374151] tracking-tight">Add New Note</h3>
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="w-full border-2 border-gray-100 rounded-xl p-4 bg-gray-50 min-h-[200px] text-[#374151] font-medium text-sm focus:bg-white focus:border-[#55C5D0] transition-all outline-none resize-none"
                    placeholder="Type your note here..."
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveNote}
                      disabled={isUpdating || !newNote.trim()}
                      className="px-6 py-2.5 bg-[#2D3139] hover:bg-black text-white rounded-lg text-xs font-bold uppercase tracking-wide transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdating ? "Saving..." : "Add Note"}
                    </button>
                  </div>
                </div>

                {/* Right: Notes History */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 h-[600px] flex flex-col">
                  <h3 className="text-lg font-bold text-[#374151] mb-4 flex items-center gap-2">
                    <span>Agent Notes</span>
                    <span className="bg-gray-200 text-gray-600 text-[10px] px-2 py-0.5 rounded-full">{propertyNotes.length}</span>
                  </h3>
                  {/* AI summary of these notes (Monday 13202770775) */}
                  {!notesLoading && (
                    <AgentNotesSummary
                      notes={propertyNotes.map((n) => ({
                        body: n.content,
                        created_at: n.created_at,
                        author: [n.author_details?.first_name, n.author_details?.last_name].filter(Boolean).join(" "),
                      }))}
                      subject={property?.address}
                      className="mb-4"
                    />
                  )}

                  <div className="flex-1 overflow-y-auto pr-2 space-y-4 custom-scrollbar">
                    {notesLoading ? (
                      <div className="text-center py-10 text-gray-400 text-sm">Loading notes...</div>
                    ) : propertyNotes.length === 0 ? (
                      <div className="text-center py-10 text-gray-400 text-sm italic">No notes added yet.</div>
                    ) : (
                      propertyNotes.map((note) => (
                        <div key={note.id} className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm group">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              {note.author?.profile_image ? (
                                <img src={note.author.profile_image} className="w-6 h-6 rounded-full" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                                  <User className="w-3 h-3 text-gray-400" />
                                </div>
                              )}
                              <span className="text-xs font-bold text-[#374151]">{note.author?.name || 'Unknown User'}</span>
                            </div>
                            <span className="text-[10px] text-gray-400">{new Date(note.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="text-sm text-gray-600 leading-relaxed">{note.content}</p>
                          <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity mt-2">
                            <button onClick={() => handleDeleteNote(note.id)} className="text-red-400 hover:text-red-500 p-1">
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'documents' && <PropertyDocumentsPage />}
          {activeTab === 'buyer_match' && (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 shadow-sm animate-in zoom-in-95 duration-500">
              <div className="border-b border-gray-100 pb-8 mb-8">
                <h3 className="text-lg font-bold text-[#374151] mb-1">Buyer Matches</h3>
                <p className="text-xs text-[#374151] opacity-60 font-medium">Potential buyers whose preferences match this property's attributes.</p>
              </div>

              {buyerMatchesLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55C5D0]"></div>
                </div>
              ) : buyerMatches.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-gray-100 rounded-xl bg-gray-50/50">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-sm mb-6">
                    <Users2 className="w-10 h-10 text-[#55C5D0]" />
                  </div>
                  <h4 className="text-base font-bold text-[#374151] mb-2">No Buyer Matches Found</h4>
                  <p className="text-sm text-[#374151] opacity-50 font-medium mb-8 max-w-md mx-auto leading-relaxed">
                    We currently haven't found any buyers in your database whose criteria specifically match this property.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {buyerMatches.map((match) => (
                    <div key={match.id} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-[#55C5D0]/30 hover:shadow-md transition-all group">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-white border border-gray-100 flex items-center justify-center overflow-hidden">
                          {match.profile_image ? (
                            <img src={match.profile_image} className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-6 h-6 text-gray-300" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-bold text-[#374151]">{match.name}</h4>
                          <span className="text-[10px] font-bold text-cyan-600 uppercase tracking-widest">{match.match_score}% Match Score</span>
                        </div>
                      </div>

                      <div className="space-y-3 mb-6">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Mail size={12} /> {match.email}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Phone size={12} /> {match.phone}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Match Reasons:</span>
                        {match.match_reasons?.map((reason, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-[11px] font-medium text-gray-600 bg-white px-3 py-1.5 rounded-lg border border-gray-200/50">
                            <CheckCircle2 size={12} className="text-green-500" />
                            {reason}
                          </div>
                        ))}
                      </div>

                      <Link to={`/leads?id=${match.id}`} className="mt-6 block w-full py-3 bg-white border border-gray-200 text-[#374151] rounded-xl text-[10px] font-black uppercase tracking-widest text-center group-hover:bg-[#374151] group-hover:text-white group-hover:border-[#374151] transition-all">
                        View Lead Profile
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <AddOwnerModal
        isOpen={showOwnerModal}
        onClose={() => setShowOwnerModal(false)}
        onSave={handleSaveOwner}
        propertyId={params.id}
      />

      <AddPurchaserModal
        isOpen={showPurchaserModal}
        onClose={() => setShowPurchaserModal(false)}
        onSave={handleSavePurchaser}
        propertyId={params.id}
      />

      <LeadPreviewModal
        isOpen={!!editingEnquiry}
        onClose={() => setEditingEnquiry(null)}
        enquiry={editingEnquiry}
        mode="view_enquiry"
        loading={isUpdatingEnquiry}
        onSaveEnquiry={handleUpdateEnquiry}
        currentUserName={user?.name || user?.email}
        onAddNote={async (finalNotes) => {
          if (!editingEnquiry?.id) return;
          try {
            await updateEnquiry(editingEnquiry.id, { agent_notes: finalNotes });
            setEnquiries(prev => prev.map(e => e.id === editingEnquiry.id ? { ...e, agent_notes: finalNotes } : e));
            setEditingEnquiry(prev => prev ? { ...prev, agent_notes: finalNotes } : null);
            toast.success("Note added!");
          } catch (err) {
            toast.error("Failed to add note");
          }
        }}
      />

      <LeadPreviewModal
        isOpen={!!viewingOffer}
        onClose={() => setViewingOffer(null)}
        offer={viewingOffer}
        mode="view_offer"
        loading={isUpdatingOffer}
        currentUserName={user?.name || user?.email}
        onSaveOffer={async (formData) => {
          if (!viewingOffer) return;
          setIsUpdatingOffer(true);
          try {
            const { updateOffer } = await import('../../../api/propertyManagement');
            const updatePayload = {
              status: formData.status,
              agent_notes: formData.agent_notes,
              offer_amount: formData.offer_amount,
              settlement_days: formData.settlement_days,
              custom_settlement_date: formData.custom_settlement_date || null,
              deposit_amount: formData.deposit_amount,
              counter_amount: formData.counter_amount || null,
            };
            const response = await updateOffer(viewingOffer.id, updatePayload);
            const updatedData = response?.data || updatePayload;
            setOffers((prev) =>
              prev.map((o) => o.id === viewingOffer.id
                ? { ...o, ...updatedData }
                : o
              )
            );
            toast.success('Offer updated!');
            setViewingOffer(null);
          } catch (err) {
            console.error('Failed to update offer:', err);
            toast.error('Failed to update offer.');
          } finally {
            setIsUpdatingOffer(false);
          }
        }}
        onAddNote={async (finalNotes) => {
          if (!viewingOffer?.id) return;
          try {
            const { updateOffer } = await import('../../../api/propertyManagement');
            await updateOffer(viewingOffer.id, { agent_notes: finalNotes });
            setOffers(prev => prev.map(o => o.id === viewingOffer.id ? { ...o, agent_notes: finalNotes } : o));
            setViewingOffer(prev => prev ? { ...prev, agent_notes: finalNotes } : null);
            toast.success("Note added!");
          } catch (err) {
            toast.error("Failed to add note");
          }
        }}
        onWorkflowOfferUpdated={(o) => {
          if (!o?.id) return;
          setViewingOffer((prev) => (prev?.id === o.id ? { ...prev, ...o } : prev));
          setOffers((prev) => prev.map((x) => (x.id === o.id ? { ...x, ...o } : x)));
        }}
      />
    </div >
  );
}